import { TUNING } from '~/config/tuning'
import { ascensionSecretBoss } from '~/data/ascensions'
import { getCreatureDef } from '~/data/creatures'
import { PASSIVE_VALUES, PASSIVES, type PassiveDef } from '~/data/passives'
import { CREATURE_PASSIVES, CREATURE_PASSIVE_VALUES, getCreaturePassive } from '~/data/creaturePassives'
import { CREATURE_SKILL_VALUES, ONCE_PER_NODE, getCreatureSkillDef } from '~/data/creatureSkills'
import { TRAINED_SKILL_IDS } from '~/data/trainedSkills'
import { hasPassive, hasRelic, ownedCharmElements } from '~/data/relics'
import { hasWeeklyMod, IRON_BOSS_COVERAGE } from '~/data/weeklyMods'
import { ELEMENTS, type Creature, type ElementKey, type Mission, type RunState, type Stats } from '~/types/game'
import { ensureStarweaveNode, hasStarweave, starweaveDispatchBonus, starweaveElementBonus, starweaveFailureReduction, starweaveFightBonus, starweaveOnDispatch, starweaveOnFailedReturn, starweaveFailureBonus, starweaveFailureCost, starweaveOnSuccess, starweaveSpeedBonus, starweaveWindfallChance } from '~/utils/starweaveRun'

export const MIN_EXPIRE_TIMER = 3
export const MIN_FIGHT_TIMER = 1

export function emptyStats(): Stats {
  return { fire: 0, water: 0, grass: 0, lightning: 0, earth: 0 }
}

// ---------- Trained skills (v0.79) ----------

/** Whether this creature learned a 🎓 skill at a Training mission. */
export function hasTrainedSkill(creature: Creature, id: string): boolean {
  return creature.learnedSkills?.includes(id) ?? false
}

/** Whether anyone on the team knows the skill. */
export function teamHasTrained(team: Creature[], id: string): boolean {
  return team.some(c => hasTrainedSkill(c, id))
}

// ---------- Injuries & ascensions (v0.60) ----------

/** Injuries run when the global flag is on OR the run is Ascension 1+. */
export function injuriesActive(run?: RunState | null): boolean {
  return TUNING.injury.enabled || (run?.ascension ?? 0) >= 1
}

/** Current stacks (legacy saves carry only the boolean). */
export function injuryStacks(creature: Creature): number {
  return creature.injuries ?? (creature.injured ? 1 : 0)
}

/** 😴🕯️ v5.29 (per Javi): Hexa and the Sandman ramp every OTHER act,
 * capped at 4 — acts 1-2: one cursed slot/sleeper, acts 3-4: two,
 * acts 5-6: three, act 7+ (deep gauntlet): four. The Sandman still
 * always leaves one ally awake. ONE curve for both bosses.
 * 🧮 v7.52 (per Javi: a run that never recruited met two sleepers with
 * three creatures at the Act 3 boss): the count also follows the ROSTER
 * — never more than the team can spare past three, so at least three
 * allies stay awake / uncursed, and a team of three or four loses one.
 * Callers with no run (bare previews, tests) keep the act curve. */
export function bossAfflictionCount(act: number | null | undefined, rosterSize?: number | null): number {
  const byAct = Math.max(1, Math.min(4, Math.ceil((act ?? 1) / 2)))
  if (rosterSize == null) return byAct
  return Math.min(byAct, Math.max(1, rosterSize - 3))
}

/** 😴 The Sandman's sleepers this attempt — plural since v5.27. The
 * legacy singular field (a save mid-boss-fight) still counts. */
export function knockedOutIds(mission: Mission): string[] {
  const ids = [...(mission.knockedOutCreatureIds ?? [])]
  if (mission.knockedOutCreatureId && !ids.includes(mission.knockedOutCreatureId)) {
    ids.push(mission.knockedOutCreatureId)
  }
  return ids
}

/** 💫 v7.53 (per Javi): who the Sandman has asleep RIGHT NOW — the
 * sleepers of every knockout boss mission still in play on the field
 * (waiting, walking or fighting; a resolved one is fading out and its
 * reform carries the new sleepers). Marks the roster card and the dock,
 * so a lullaby no longer reads as an injury outside the mission sheet. */
export function sandmanSleepers(encounter: { missions: Mission[] } | null | undefined): Set<string> {
  const out = new Set<string>()
  for (const mission of encounter?.missions ?? []) {
    if (mission.bossSkill !== 'knockout' || mission.tier !== 'boss') continue
    if (mission.status !== 'waiting' && mission.status !== 'traveling' && mission.status !== 'assigned') continue
    for (const id of knockedOutIds(mission)) out.add(id)
  }
  return out
}

/** A failed mission's toll: one stack normally, piling up from
 * Ascension 9 (Broken Bones, v1.75 swap) up. */
export function injureCreature(creature: Creature, run?: RunState | null) {
  if (!injuriesActive(run)) return
  // Stacking moved A7 → A9 in the v1.75 ladder swap.
  const stacking = (run?.ascension ?? 0) >= 9
  creature.injuries = stacking ? injuryStacks(creature) + 1 : 1
  creature.injured = true
}

/** Removes ONE stack (A9+ cures peel; below that this heals fully). */
export function cureCreatureStack(creature: Creature) {
  creature.injuries = Math.max(0, injuryStacks(creature) - 1)
  creature.injured = creature.injuries > 0
}

export function healInjuriesFully(creature: Creature) {
  creature.injuries = 0
  creature.injured = false
}

/** Below Ascension 3 (Deep Wounds) the classic rule holds: a success
 * shakes the injury off. */
export function successClearsInjury(run?: RunState | null): boolean {
  return (run?.ascension ?? 0) <= 2
}

/** Injuries end with the node below Ascension 5 (Scars); from there
 * they last the whole run. */
export function nodeEndClearsInjuries(run?: RunState | null): boolean {
  return (run?.ascension ?? 0) <= 4
}

export interface CreatureSkill {
  kind: 'surge' | 'dual' | 'none'
  /** The element(s) boosted while the surge is armed. */
  elements: ElementKey[]
  /** Bonus per listed element. */
  bonus: number
  /** 🔋 True when Overcharge Cell LENDS this surge to a surge-less
   * creature — a lent surge is never doubled (v1.95, per Javi). */
  granted?: boolean
}

/**
 * A creature's innate on-use skill, fixed by its BASE statline (so Ember
 * Cub is always "Fire Surge" regardless of leveling): one clear top
 * element → Elemental Surge (+2 to it on the next mission); tied top
 * elements → Dual Surge (+1 to each, next mission). Cooldown starts when
 * the creature returns home.
 */
export function creatureSkill(creature: Creature, run?: RunState | null): CreatureSkill {
  const def = getCreatureDef(creature.defId)
  // 🌈 Prism Surge (v2.28, per Javi): the trained upgrade replaces the
  // natural Surge outright — +3 to EVERY element — and even noSurge
  // creatures gain the button. v4.24 (per Javi: "overcharge cell is not
  // correctly doubling prism surge"): NOT granted — the Cell doubles it
  // like any owned surge; only LENT surges (below) stay undoubled.
  // Passive logic must not see this override — baseMainElements() only.
  if (hasTrainedSkill(creature, 'prism-surge')) {
    return { kind: 'surge', elements: [...ELEMENTS], bonus: TUNING.skills.omniSurgeBonus }
  }
  if (def.noSurge) {
    // 🔋 Overcharge Cell lends surge-less creatures a NORMAL surge
    // on their best allocated element — never doubled (v1.95).
    if (run && hasRelic(run, 'relic-overcharge')) {
      const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
      if (creature.stats[top] > 0) {
        return { kind: 'surge', elements: [top], bonus: TUNING.skills.surgeBonus, granted: true }
      }
    }
    return { kind: 'none', elements: [], bonus: 0 }
  }
  const base = def.stats
  const max = Math.max(...ELEMENTS.map(e => base[e]))
  const mains = ELEMENTS.filter(e => base[e] === max && max > 0)
  return mains.length <= 1
    ? { kind: 'surge', elements: mains, bonus: TUNING.skills.surgeBonus }
    : { kind: 'dual', elements: mains, bonus: TUNING.skills.dualSurgeBonus }
}

/** 🔋 v4.72 (per Javi: "make sure Aurora Serpent works with Overcharge
 * Cell and Prism Surge"): the Cell doubles any OWNED surge — never a
 * LENT one — and every surge-derived effect must agree. effectiveStats,
 * the Serpent's cascade, Tidemother's deluge and Catchfire's burn-in
 * all multiply through here. */
export function surgeCellMult(skill: CreatureSkill, run?: RunState | null): number {
  return run && hasRelic(run, 'relic-overcharge') && !skill.granted ? 2 : 1
}

/** The BASE statline's top elements — what creatureSkill returned before
 * trained upgrades could rewrite it. Passive nature checks key off this,
 * never the live skill (a Prism Surge upgrade must not turn every element
 * into a passive-eligible "nature"). */
export function baseMainElements(creature: Creature): ElementKey[] {
  // v3.12 (per Javi): EVERY creature has a nature — skill carriers
  // included. Their surge slot holds the skill, but their top base
  // element still awakens the default elemental passive at level 5.
  const def = getCreatureDef(creature.defId)
  const max = Math.max(...ELEMENTS.map(e => def.stats[e]))
  return ELEMENTS.filter(e => def.stats[e] === max && max > 0)
}

/**
 * Evolving passives (v0.59): a creature whose base main element is STILL
 * its highest allocated stat awakens a passive at level 5, strengthening
 * at 7 and 9. Straying from its nature (leveling another element past
 * the main) suppresses it — stay true, get rewarded.
 */
export interface CreaturePassive {
  /** Undefined for a creature's OWN passive (v2.80) — it has no element,
   * so the elemental effect hooks skip it automatically. */
  element?: ElementKey
  /** 1 at level 5+, then +1 every two levels, capped at the passive's
   * natural tier count — plus one more with 🌌 Apotheosis. */
  tier: number
  def: PassiveDef
}

/** Passive tier from level. 🌌 Apotheosis (v0.91) unlocks ONE EXTRA tier
 * past the passive's last, at level 11.
 *
 * v2.73 (per Javi, planning the creature expansion): the count is read
 * from the passive, not assumed to be 4. The five elemental passives run
 * three natural tiers so Apotheosis is their fourth, but custom passives
 * define 2–4 natural tiers and each still gets exactly one on top —
 * "the tier after the last one", never "tier 4". */
/** 🌌 Where a passive's Apotheosis engages: two past the last natural
 * unlock, unless the def pins it (v5.32). */
export function apotheosisLevelFor(def: PassiveDef): number {
  return def.apotheosisAt ?? ((def.unlockLevels[def.unlockLevels.length - 1] ?? 9) + 2)
}

export function passiveTierFor(creature: Creature, def?: PassiveDef): number {
  // v2.80: pacing is read from the passive. The elemental five declare
  // [5, 7, 9], which reproduces the old fixed ladder exactly; creature
  // passives set their own (Morning Chorus blooms at 2/4/6, Rootstock
  // waits at 1/9). Returns 0 while the passive is still dormant.
  const levels = def?.unlockLevels ?? [5, 7, 9]
  const natural = levels.length
  // Apotheosis sits two levels past the LAST natural tier (or the def's
  // pinned apotheosisAt, v5.32), so a passive with more natural tiers
  // waits longer for it. Three natural tiers at 5/7/9 → level 11,
  // exactly where the elemental five already unlock it.
  const apotheosisAt = def ? apotheosisLevelFor(def) : (levels[natural - 1] ?? 9) + 2
  if (creature.level >= apotheosisAt && hasTrainedSkill(creature, 'apotheosis')) {
    return natural + 1
  }
  let tier = 0
  for (const level of levels) if (creature.level >= level) tier++
  return tier
}

/** The creature's OWN passive definition, if it carries one.
 * 🎭 Understudy's copied passive outranks the creature's innate one. */
export function ownPassiveDef(creature: Creature): PassiveDef | null {
  return getCreaturePassive(creature.passiveOverride)
    ?? getCreaturePassive(creature.routinePassive)
    ?? getCreaturePassive(getCreatureDef(creature.defId).passive)
}

export function creaturePassive(creature: Creature): CreaturePassive | null {
  // 🧬 v2.80: a creature carrying its OWN passive uses that instead of
  // the elemental one — Javi's rule is one set or the other, never both.
  // It has no "stay true to your nature" condition, because it has no
  // nature to stray from; it simply unlocks on its own level ladder.
  const own = ownPassiveDef(creature)
  if (own) {
    const tier = passiveTierFor(creature, own)
    return tier > 0 ? { element: undefined, tier, def: own } : null
  }
  const baseMains = baseMainElements(creature)
  const currentMax = Math.max(...ELEMENTS.map(e => creature.stats[e]))
  const match = baseMains.find(e => creature.stats[e] === currentMax)
  if (!match) return null
  const tier = passiveTierFor(creature, PASSIVES[match])
  return tier > 0 ? { element: match, tier, def: PASSIVES[match] } : null
}

/** What a creature WILL awaken at level 5 (for draft/reference cards). */
export function potentialPassives(baseMains: ElementKey[], defId?: string): PassiveDef[] {
  // v2.80: a creature with its own passive advertises THAT, not the
  // elemental one it will never awaken.
  const own = defId ? getCreaturePassive(getCreatureDef(defId).passive) : null
  if (own) return [own]
  return baseMains.map(e => PASSIVES[e])
}

/** ⚡ v4.89 (perf review #3): creaturePassives is evaluated ~80× per
 * tick and ~160× per roster render, always answering the same question
 * — memoize per creature, keyed on everything the answer depends on
 * (level, the five stats, trained-skill flags, override copies). The
 * key reads the reactive fields each call, so Vue dependency tracking
 * inside computeds is preserved; only the derivation is skipped. */
const passivesMemo = new WeakMap<Creature, { key: string; out: CreaturePassive[] }>()

function passivesKey(creature: Creature): string {
  const s = creature.stats
  return `${creature.defId}|${creature.level}|${s.fire},${s.water},${s.grass},${s.lightning},${s.earth}`
    + `|${creature.passiveOverride ?? ''}|${creature.routinePassive ?? ''}`
    + `|${hasTrainedSkill(creature, 'apotheosis') ? 1 : 0}${hasTrainedSkill(creature, 'second-calling') ? 1 : 0}`
}

/** ALL live passives (v0.81): the natural one, plus 🌗 Second Calling's
 * second-highest-element passive. Consumers iterate this, never
 * creaturePassive directly, so trained expansions stay in one place. */
export function creaturePassives(creature: Creature): CreaturePassive[] {
  const key = passivesKey(creature)
  const hit = passivesMemo.get(creature)
  if (hit && hit.key === key) return hit.out
  const out = computeCreaturePassives(creature)
  passivesMemo.set(creature, { key, out })
  return out
}

function computeCreaturePassives(creature: Creature): CreaturePassive[] {
  const out: CreaturePassive[] = []
  const primary = creaturePassive(creature)
  if (primary) out.push(primary)
  if (hasTrainedSkill(creature, 'second-calling') && creature.level >= 5) {
    // v3.12 (per Javi): a creature with its OWN passive calls its BASE
    // element's passive — the default one its nature would have granted.
    // Ties break toward the base main it has leveled the highest.
    if (ownPassiveDef(creature)) {
      const base = [...baseMainElements(creature)]
        .sort((a, b) => creature.stats[b] - creature.stats[a])[0]
      if (base) {
        out.push({ element: base, tier: passiveTierFor(creature, PASSIVES[base]), def: PASSIVES[base] })
      }
      return out
    }
    // v0.95 fix (Javi's screenshot): the strongest element BEYOND its
    // nature. With the natural passive active that's the next-best
    // element (as before); with it DORMANT (strayed), the old
    // "second-highest" literally re-picked the base main — granting the
    // nature's passive while the creature's dominant element gave
    // nothing. Now a strayed water-topped fire creature calls WATER.
    const excluded = primary?.element ? [primary.element] : baseMainElements(creature)
    const second = ELEMENTS
      .filter(e => !excluded.includes(e) && creature.stats[e] > 0)
      .sort((a, b) => creature.stats[b] - creature.stats[a])[0]
    if (second) {
      // Shares the tier ladder — 🌌 Apotheosis lifts BOTH passives to T4.
      out.push({ element: second, tier: passiveTierFor(creature, PASSIVES[second]), def: PASSIVES[second] })
    }
  }
  return out
}

// ---------- 🧬 Creature passives (v2.80) ----------

/** The tier of a NAMED creature passive on this creature, 0 if dormant
 * or if it carries a different passive entirely. */
export function creaturePassiveTier(creature: Creature, id: string): number {
  for (const passive of creaturePassives(creature)) {
    if (passive.def.id === id) return passive.tier
  }
  return 0
}

/** True once the tier is past the passive's last NATURAL one. */
export function isApotheosis(id: string, tier: number): boolean {
  const def = CREATURE_PASSIVES[id]
  return !!def && tier > def.unlockLevels.length
}

/** Reads a per-tier knob, clamped (Apotheosis is the last entry). */
function tierValue(values: readonly number[], tier: number): number {
  if (tier <= 0) return 0
  return values[Math.min(tier, values.length) - 1] ?? 0
}

/** Everyone on the team (or roster) whose passive is `id`, with its tier. */
export function passiveHolders(team: Creature[], id: string): Array<{ creature: Creature; tier: number }> {
  const out: Array<{ creature: Creature; tier: number }> = []
  for (const creature of team) {
    const tier = creaturePassiveTier(creature, id)
    if (tier > 0) out.push({ creature, tier })
  }
  return out
}

/**
 * 😤 Grit: missions where it is the ONLY creature gain chance per point
 * of ⚔️ Fight it carries. Apotheosis counts as solo on a full team.
 */
export function gritChanceBonus(team: Creature[], run?: RunState | null): number {
  let bonus = 0
  for (const { creature, tier } of passiveHolders(team, 'grit')) {
    if (team.length > 1 && !isApotheosis('grit', tier)) continue
    bonus += tierValue(CREATURE_PASSIVE_VALUES.grit, tier) * (creatureFightStat(creature, run) + starweaveDispatchBonus(creature, team, run).fight)
  }
  return bonus
}

/**
 * 🌄 First Light: the FIRST mission dispatched each node that includes
 * it. Apotheosis widens that to the node's first mission whether or not
 * it joins, so the caller passes the roster for that check.
 */
export function firstLightBonus(
  team: Creature[],
  roster: Creature[],
  alreadyDispatched: boolean,
): { chance: number; haste: number } {
  if (alreadyDispatched) return { chance: 0, haste: 0 }
  let chance = 0
  let haste = 0
  const seen = new Set<string>()
  for (const creature of [...team, ...roster]) {
    if (seen.has(creature.id)) continue
    seen.add(creature.id)
    const tier = creaturePassiveTier(creature, 'first-light')
    if (!tier) continue
    const onTeam = team.some(c => c.id === creature.id)
    if (!onTeam && !isApotheosis('first-light', tier)) continue
    chance = Math.max(chance, tierValue(CREATURE_PASSIVE_VALUES.firstLightChance, tier))
    haste = Math.max(haste, tierValue(CREATURE_PASSIVE_VALUES.firstLightHaste, tier))
  }
  return { chance, haste }
}

/** 🛡️ Thornmail: extra failure-damage reduction on missions it joins
 * (Apotheosis nullifies the damage), plus the XP a failure still pays. */
export function thornmailReduction(team: Creature[]): number {
  let reduction = 0
  for (const { tier } of passiveHolders(team, 'thornmail')) {
    reduction += tierValue(CREATURE_PASSIVE_VALUES.thornmail, tier)
  }
  return reduction
}

export function thornmailXp(team: Creature[]): number {
  return passiveHolders(team, 'thornmail').length > 0 ? CREATURE_PASSIVE_VALUES.thornmailXp : 0
}

/** 🌅 Morning Chorus: the first N missions of an encounter last longer. */
export function morningChorusBonus(roster: Creature[], spawnIndex: number): number {
  let bonus = 0
  for (const { tier } of passiveHolders(roster, 'morning-chorus')) {
    const count = tierValue(CREATURE_PASSIVE_VALUES.morningChorusCount, tier)
    if (spawnIndex >= count) continue
    bonus = Math.max(bonus, tierValue(CREATURE_PASSIVE_VALUES.morningChorusSeconds, tier))
  }
  return bonus
}

/**
 * 💨 Tailwind: Speed for creatures dispatched AFTER it this node.
 * `armed` is what earlier Tailwind dispatches banked (EncounterState
 * .tailwindSpeed). Its OWN trip only benefits at Apotheosis, which
 * covers "every creature dispatched this node, before it as well".
 */
export function tailwindSpeed(team: Creature[], armed: number): number {
  let own = 0
  for (const { tier } of passiveHolders(team, 'tailwind')) {
    if (isApotheosis('tailwind', tier)) {
      own = Math.max(own, tierValue(CREATURE_PASSIVE_VALUES.tailwind, tier))
    }
  }
  return Math.max(armed, own)
}

/** What a dispatching team BANKS for the rest of this node's trips. */
export function tailwindArmedBy(team: Creature[], current: number): number {
  let banked = current
  for (const { tier } of passiveHolders(team, 'tailwind')) {
    banked = Math.max(banked, tierValue(CREATURE_PASSIVE_VALUES.tailwind, tier))
  }
  return banked
}

/**
 * 🌋 Ashbed: a mission it joined FAILED — bank permanent 🔥. Apotheosis
 * spreads the same gain to everyone who fought beside it.
 */
export function ashbedOnFailure(creature: Creature, team: Creature[]) {
  const tier = creaturePassiveTier(creature, 'ashbed')
  if (!tier) return
  const gain = tierValue(CREATURE_PASSIVE_VALUES.ashbed, tier)
  creature.stats.fire += gain
  if (!isApotheosis('ashbed', tier)) return
  for (const mate of team) {
    if (mate.id !== creature.id) mate.stats.fire += gain
  }
}

/**
 * 🔧 Scrapfire: ANY mission expiring on the field feeds every Scrapfire
 * on the ROSTER — it does not have to be dispatched. Apotheosis feeds
 * its highest element as well as its lowest.
 */
export function scrapfireOnExpiry(roster: Creature[]) {
  for (const { creature, tier } of passiveHolders(roster, 'scrapfire')) {
    const gain = tierValue(CREATURE_PASSIVE_VALUES.scrapfire, tier)
    // "Lowest above 0" — a zeroed element is not part of its nature.
    const live = ELEMENTS.filter(e => creature.stats[e] > 0)
    if (live.length === 0) continue
    const lowest = live.reduce((a, b) => (creature.stats[b] < creature.stats[a] ? b : a))
    creature.stats[lowest] += gain
    if (isApotheosis('scrapfire', tier)) {
      const highest = live.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
      creature.stats[highest] += gain
    }
  }
}

/**
 * 🌳 Rootstock: completing a node feeds the whole team. Returns the gold
 * to add; XP is granted directly. Apotheosis is handled at rest stops.
 */
export function rootstockOnNodeComplete(roster: Creature[], state?: RunState | null): number {
  let gold = 0
  for (const { tier } of passiveHolders(roster, 'rootstock')) {
    const xp = tierValue(CREATURE_PASSIVE_VALUES.rootstockXp, tier)
    for (const mate of roster) grantXp(mate, xp, state)
    gold += tierValue(CREATURE_PASSIVE_VALUES.rootstockGold, tier)
  }
  return gold
}

/**
 * 🤑 Avarice: gold held at node end becomes permanent stats. Returns the
 * number of creatures that grew, for the caller to report.
 */
export function avariceOnNodeComplete(roster: Creature[], gold: number): number {
  const points = Math.floor(gold / 25)
  if (points <= 0) return 0
  let grew = 0
  for (const creature of roster) {
    if (!hasTrainedSkill(creature, 'avarice')) continue
    const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
    creature.stats[top] += points
    grew++
  }
  return grew
}

/**
 * 🎭 Understudy: at node end, permanently copy a teammate's passive.
 * Only creature passives can be copied — the elemental five are awarded
 * by nature and would be meaningless on a creature of another element.
 */
export function understudyOnNodeComplete(roster: Creature[]) {
  for (const creature of roster) {
    if (!hasTrainedSkill(creature, 'understudy')) continue
    // 🎭 v5.32 (per Javi's chain report): copy the teammate's INNATE
    // passive, never its live override — two understudies mirroring
    // each other propagated every later change down the chain (his
    // 3rd creature kept following whatever Ouroboros turned into).
    const source = roster.find(mate =>
      mate.id !== creature.id && !!getCreatureDef(mate.defId).passive)
    if (source) creature.passiveOverride = getCreatureDef(source.defId).passive
  }
}

/**
 * 🩸 Martyr: its failure hardens the whole company.
 *
 * v3.81 (Javi: "martyr did not work… the team doesnt need to be deployed
 * with the creature that failed"): the martyr must still be ON the failed
 * mission, but the creatures that HARDEN are the whole roster, not just
 * the squad that walked out with it. Deployed-only made the skill close
 * to a no-op in practice: a Martyr build wants to fail, and the cheapest
 * way to fail is to send it out alone or nearly so — precisely when there
 * is nobody standing beside it to receive the gift. `roster` is optional
 * so the existing tests (and any caller without run state) still express
 * "this squad" by passing one list.
 */
export function martyrOnFailure(team: Creature[], roster?: Creature[]) {
  if (!teamHasTrained(team, 'martyr')) return
  martyrGift(roster ?? team)
}

/** 🩸 v4.65 (Javi's lose-on-purpose buff): the rot pays too — any
 * regular expiry gifts the roster, no martyr aboard required (nothing
 * is aboard an expired mission by definition). v5.30 (per Javi): the
 * expiry gift sharpens only the SPIKE — +1 highest, no lowest patch.
 * Failures keep both halves. */
export function martyrOnExpiry(roster: Creature[]) {
  if (!roster.some(c => hasTrainedSkill(c, 'martyr'))) return
  martyrGift(roster, { topOnly: true })
}

/** The gift itself: +1 highest AND +1 lowest (v4.65 — the lowest half
 * patches coverage instead of only sharpening spikes). +1 however many
 * martyrs shared the loss — sacrifice doesn't stack (v4.16).
 * v5.08 (per Javi): the lowest is the lowest ABOVE 0 — a dead element
 * stays dead, the gift sharpens a build instead of rainbowing it
 * (Osmosis's rule, applied here). */
function martyrGift(receivers: Creature[], opts: { topOnly?: boolean } = {}) {
  for (const creature of receivers) {
    if (hasTrainedSkill(creature, 'martyr')) continue
    const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
    const live = ELEMENTS.filter(e => creature.stats[e] > 0)
    if (live.length === 0) continue
    const low = live.reduce((a, b) => (creature.stats[b] < creature.stats[a] ? b : a))
    creature.stats[top] += 1
    if (!opts.topOnly && low !== top) creature.stats[low] += 1
  }
}

/** 🩹 Field Medic (v4.65, unbanked for the lose-on-purpose kit): every
 * medic resting at home patches its LEVEL in HP per failure — half per
 * expiry (parking costs no dispatch and no injury) — and once per node
 * grows max HP by its level. The once-per-node meter is what keeps
 * "fail forever" from minting infinite max HP. */
export function fieldMedicOnLoss(state: RunState, kind: 'failure' | 'expiry') {
  const medics = state.roster.filter(c => c.available && hasTrainedSkill(c, 'field-medic'))
  const enc = state.encounter
  for (const medic of medics) {
    if (enc && !(enc.medicMaxHpPaid ?? []).includes(medic.id)) {
      enc.medicMaxHpPaid = [...(enc.medicMaxHpPaid ?? []), medic.id]
      state.maxHp += medic.level
    }
    const heal = kind === 'failure' ? medic.level : Math.ceil(medic.level / 2)
    state.hp = Math.min(state.maxHp, state.hp + heal)
  }
}

/** 🧘 Deep Sit: 🪨 accrued while resting, spent on its next fight. */
export function deepSitBonus(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'deep-sit')
  if (!tier) return 0
  const per = tierValue(CREATURE_PASSIVE_VALUES.deepSit, tier)
  const ticks = Math.floor((creature.restSeconds ?? 0) / 10)
  return per * ticks
}


// ---------- 🔁 Node lifecycle payouts (v2.85) ----------

/**
 * Every creature / relic / trained-skill payout that fires on a LIFECYCLE
 * EVENT lives here rather than inline in useEncounter.
 *
 * Why: `scripts/difficulty.ts` and `scripts/simulate.ts` MIRROR the
 * encounter loop instead of importing it, so anything written inline in
 * useEncounter is invisible to the balance simulations. v2.84 proved the
 * cost — Sundial's window was doubled and its simulated score moved by
 * 0.01, because the sim never applied its payout at all.
 *
 * Rule going forward: an effect that fires on dispatch, expiry, failure,
 * success or node completion belongs in this section. The sims call the
 * same five functions the game does, so they cannot drift again.
 */

/** 🌞 Sundial's brinkmanship window, in seconds. */
export const SUNDIAL_WINDOW = 10

/** Dispatch: Sundial's flag, Tailwind's bank, and the node's
 * first-dispatch marker. Call AFTER travel speed is computed
 * (Tailwind must not speed up its own carrier's trip). */
export function onMissionDispatched(state: RunState | null | undefined, mission: Mission, team: Creature[]) {
  const enc = state?.encounter
  if (!state || !enc) return
  starweaveOnDispatch(state, team)
  mission.sundialBoosted = hasRelic(state, 'relic-sundial') && mission.expireTimer < SUNDIAL_WINDOW
  // 🪙 Tithe: the primed boost attaches to THIS mission and is spent.
  if (enc.tithePrimed) {
    mission.titheBoosted = true
    enc.tithePrimed = false
  }
  // 🌊 Deluge: dispatched inside the window keeps the boost for good.
  if ((enc.delugeSeconds ?? 0) > 0) mission.delugeBoosted = true
  // 🔥 Catchfire (v3.53; fields still say 'giggle' so saves and locale
  // keys survive). v4.66 (per Javi): the spark BURNS IN — once per node,
  // a surged dispatch grants the surge stats PERMANENTLY. T1 pays +1 of
  // the element to one random teammate; T2 the full surge bonus; the
  // Apotheosis pays the whole roster.
  for (const moth of team) {
    const tier = creaturePassiveTier(moth, 'infectious-giggle')
    if (!tier || !moth.surgePrimed || moth.giggleSpent) continue
    const skill = creatureSkill(moth, state)
    if (skill.kind === 'none' || skill.elements.length === 0) continue
    const apo = isApotheosis('infectious-giggle', tier)
    // 🔋 v4.72: a Cell-doubled (or Prism) surge burns in at full effect.
    const amount = tier >= 2 ? skill.bonus * surgeCellMult(skill, state) : 1
    const pool = (apo ? state.roster : team).filter(c => c.id !== moth.id)
    const targets = apo ? pool : pool.length ? [pool[Math.floor(Math.random() * pool.length)]!] : []
    if (targets.length === 0) continue
    for (const target of targets) {
      for (const element of skill.elements) target.stats[element] += amount
    }
    moth.giggleSpent = true
  }
  // 🪞 v4.86: an Echopus remembers who it fights beside — the node-end
  // keeper banks only from these partners (player-steerable).
  for (const holder of team) {
    if (!creaturePassiveTier(holder, 'reflection')) continue
    const partners = new Set(holder.reflectionPartners ?? [])
    for (const mate of team) if (mate.id !== holder.id) partners.add(mate.id)
    holder.reflectionPartners = [...partners]
  }
  enc.tailwindSpeed = tailwindArmedBy(team, enc.tailwindSpeed ?? 0)
  // 🎭 Routine copies whoever went out right before this team - and the
  // Mime studies the node's OPENING act, so it must run while
  // firstDispatchDone is still false. v7.14 (code review): it ran after
  // the flag flipped, so the copy never happened in real play; only the
  // preview (withPredictedRoutine) and a direct-call unit test saw it.
  routineOnDispatch(state, team)
  // 🌄 v7.14: the mission remembers whether it opened the node, so First
  // Light resolves against the fact it previewed (passiveChanceBonus).
  mission.firstDispatch = !enc.firstDispatchDone
  enc.firstDispatchDone = true
  // 🪶 A tier-1 Featherweight spends its pass only when it was needed.
  if (team.length > missionSlots(mission, state)) {
    for (const creature of team) {
      if (creaturePassiveTier(creature, 'featherweight') === 1 && !creature.featherweightUsed) {
        creature.featherweightUsed = true
        break
      }
    }
  }
}

/** Expiry: 🔧 Scrapfire feeds off every mission lost to the clock. */
export function onMissionExpired(state: RunState | null | undefined, mission?: Mission) {
  if (!state) return
  // 🚱 v4.00 (per Javi: "Pyrelord Ossuary should not get stacks from
  // bosses expiring — dr. vex for example"): boss-tier expiries and boss
  // MINIONS feed nothing. The Amalgam reforms on a 240s fuse and Vex's
  // meddling respawns forever, so a camped Vex fight was an infinite
  // farm for every expiry-fed passive — Cremation was the one reported,
  // but Scrapfire and Will-o'-Wisp drank from the same tap.
  if (mission && (mission.tier === 'boss' || mission.minion)) return
  // 🩸📖🔥😤 v4.65 (Javi's lose-on-purpose buff): the rot itself pays,
  // roster-wide — at HALF the failure rate, since parking costs no
  // dispatch and no injury. Failure keeps the premium payouts.
  // 💰 v5.30 (per Javi): a GOLD lapse is free — no damage dealt, so no
  // rot payout for Pain into Power or the Martyr's gift. The other
  // expiry-fed effects keep their tap.
  const freeLapse = !!mission?.goldReward
  if (!freeLapse) martyrOnExpiry(state.roster)
  for (const creature of state.roster) {
    if (hasTrainedSkill(creature, 'tough-learner')) grantXp(creature, 3, state)
    if (!freeLapse && hasTrainedSkill(creature, 'pain-into-power')) {
      for (const element of ELEMENTS) creature.stats[element] += 1
    }
    if (hasTrainedSkill(creature, 'grudge')) creature.grudgePrimed = true
  }
  fieldMedicOnLoss(state, 'expiry')
  scrapfireOnExpiry(state.roster)
  // ⚱️ Cremation: every REGULAR expiry anywhere feeds every Ossuary.
  // v4.66 (per Javi): the lowest element drinks too.
  for (const { creature, tier } of passiveHolders(state.roster, 'cremation')) {
    creature.stats.fire += tierValue(CREATURE_PASSIVE_VALUES.cremation, tier)
    const low = ELEMENTS.reduce((a, b) => (creature.stats[b] < creature.stats[a] ? b : a))
    if (low !== 'fire') creature.stats[low] += tierValue(CREATURE_PASSIVE_VALUES.cremationLow, tier)
  }
  // 🏮 Will-o'-Wisp: every expiry haunts every Lantern on the roster.
  for (const { creature } of passiveHolders(state.roster, 'will-o-wisp')) {
    creature.wispGhosts = (creature.wispGhosts ?? 0) + 1
  }
}

/** 🔷/🤭/🏮 Mission-scoped riders are spent when the mission resolves. */
export function spendRideAlongs(team: Creature[]) {
  for (const creature of team) {
    creature.refractStats = undefined
    creature.giggleElement = undefined
    creature.giggleBonus = undefined
    creature.mimicElements = undefined
    creature.mimicBonus = undefined
    const tier = creaturePassiveTier(creature, 'will-o-wisp')
    if (tier && !isApotheosis('will-o-wisp', tier)) creature.wispGhosts = 0
  }
}

/**
 * 🎩 v7.79 (per Javi: "anytime khool fails (even if later composure roll results in a win) i want failure bonuses to
 * apply"): everything a failed mission PAYS the player, and nothing it costs - 🩸 Martyr, 🌋 Ashbed, Soulbind,
 * Lessons in Defeat (and Regroup armed), Team Journal's prime, 🩹 Field Medic, 📖 Tough Learner, 🔥 Pain Into Power
 * and 😤 Grudge. Returns the CONSOLATION XP per member (🛡️ Thornmail + Lessons in Defeat), granted by the caller
 * beside xpOnFailure. Never: HP, injuries, stack resets, Safety Net / Medic Band / Bulwark Drop / Controlled
 * Failure, or the resolve bookkeeping (Second Wind TOGGLES - it must never run twice). A real failure runs it
 * through onMissionFailed; a Composure rescue through onMissionRescued - once per mission either way.
 */
export function failureBonuses(team: Creature[], mission?: Mission, state?: RunState | null, armRegroup = true): number {
  const starweaveXp = state && mission && !mission.training ? starweaveFailureBonus(state, armRegroup) : 0
  martyrOnFailure(team, state?.roster)
  for (const creature of team) ashbedOnFailure(creature, team)
  if (mission) soulbindOnFailure(team, mission)
  if (state) {
    // Team Journal: a failure primes the next assignment with +10%.
    if (state.encounter && hasRelic(state, 'relic-team-journal')) state.encounter.teamJournalPrimed = true
    // 🩹 Field Medic (v4.65, unbanked): home medics patch LEVEL HP per failure and grow max HP once per node.
    fieldMedicOnLoss(state, 'failure')
    for (const creature of team) {
      // 📖 Tough Learner is a PASSIVE (v2.27, per Javi - no arming).
      if (hasTrainedSkill(creature, 'tough-learner')) grantXp(creature, 5, state)
      // 🔥 Pain Into Power is a PASSIVE (v1.89): every failure it fights through hardens it, +2 to EVERYTHING (v2.27).
      if (hasTrainedSkill(creature, 'pain-into-power')) for (const element of ELEMENTS) creature.stats[element] += 2
    }
    // 😤 Grudge: every grudge-holder on the roster seethes - their next dispatch carries +20%.
    for (const creature of state.roster) if (hasTrainedSkill(creature, 'grudge')) creature.grudgePrimed = true
  }
  return thornmailXp(team) + starweaveXp
}

/** Failure: the bonuses above plus what a failure spends. Returns the consolation XP (unchanged contract). */
export function onMissionFailed(team: Creature[], mission?: Mission, state?: RunState | null): number {
  if (state && mission && !mission.training) starweaveFailureCost(state)
  balanceOfAgesExpire(team)
  const consolation = failureBonuses(team, mission, state)
  // 🧱 Bulwark Drop absorbed this failure — spent.
  if (state?.encounter?.bulwarkArmed) state.encounter.bulwarkArmed = false
  blackholeAfterResolve(team, mission)
  consumeCreatureSkills(team)
  spendWager(team, mission)
  secondWindCycle(team)
  staticChargeOnResolve(team, false)
  cascadeAfterResolve(team)
  spendRideAlongs(team)
  return consolation
}

/**
 * 🎩 v7.79: Mr. Khool's Composure turned a FAILED roll into this win. The failure still teaches: its bonuses land
 * once, as of that roll - none of its costs (no damage, injury, Safety Net, Medic Band, Bulwark Drop, Controlled
 * Failure, stack reset or failure count). Homeward Star (a cure for a failed team's injuries) has nothing to cure
 * on a rescue, so the rescued walk home is not flagged. Call BEFORE onMissionSucceeded: its consume pass clears the Bulwark that
 * bulwarkXpShare reads, exactly as the failure branch reads it before onMissionFailed.
 */
export function onMissionRescued(state: RunState, mission: Mission, team: Creature[], xpOnFailure: number): void {
  if (mission.training) return
  const share = bulwarkXpShare(team)
  // Regroup is armed AFTER this win's own hooks (applyResolution: starweaveArmRegroup), so this win cannot spend it.
  const consolation = failureBonuses(team, mission, state, false)
  for (const creature of team) grantXp(creature, Math.round(xpOnFailure * share) + consolation, state)
}

/**
 * Success: armed creature skills are spent, permanent-growth passives
 * bank their points, and the caller gets the bonus GOLD to pay out.
 */
/** 🦕 Burden of Ages (v5.95, kit per Javi): every mission Legacy
 * completes grants permanent points in each element it required — +1 at
 * tier 1, +2 from tier 2 (level 5). At Apotheosis (GAIA’S GIFT), a
 * Legacy WAITING AT CAMP extends the same +1 growth to every other
 * team's successes. Training missions teach; they do not burden. */
export function burdenOfAgesOnSuccess(team: Creature[], mission: Mission, roster?: Creature[]) {
  if (mission.training) return
  const els = ELEMENTS.filter(el => (mission.required[el] ?? 0) > 0)
  if (els.length === 0) return
  for (const creature of team) {
    const p = creaturePassive(creature)
    if (p?.def.id !== 'burden-of-ages') continue
    const gain = p.tier >= 2 ? 2 : 1
    for (const el of els) creature.stats[el] += gain
  }
  if (!roster) return
  const gaia = roster.find(c => c.defId === 'legacy' && c.available
    && (creaturePassive(c)?.def.id === 'burden-of-ages' && (creaturePassive(c)?.tier ?? 0) >= 4)
    && !team.some(t => t.id === c.id))
  if (!gaia) return
  for (const creature of team) {
    if (creaturePassive(creature)?.def.id === 'burden-of-ages') continue
    for (const el of els) creature.stats[el] += 1
  }
}

/** 🪨 STEADY GROUND (Burden tier 3): while Legacy waits at basecamp,
 * waiting missions expire 50% slower. Multiplies the expiry tick. */
export function steadyGroundExpiryMult(state: RunState | null | undefined): number {
  const legacy = state?.roster.find(c => c.defId === 'legacy' && c.available)
  if (!legacy) return 1
  const p = creaturePassive(legacy)
  return p?.def.id === 'burden-of-ages' && p.tier >= 3 ? 0.5 : 1
}

/** ⚖️ Balance of Ages: Legacy evens its stats into a level spread;
 * the presentation reuses Equilibrium's fully-reversible machinery. */
export function useBalanceOfAges(creature: Creature): boolean {
  if (!canUseCreatureSkill(creature)) return false
  creature.equalized = true
  spendCreatureSkill(creature)
  return true
}

/** ⚖️ The balance runs out the moment its mission ends (per Javi). */
export function balanceOfAgesExpire(team: Creature[]) {
  for (const creature of team) {
    if (creature.equalized && creatureOwnSkill(creature)?.id === 'balance-of-ages') {
      creature.equalized = false
    }
  }
}

export function onMissionSucceeded(
  team: Creature[],
  mission?: Mission,
  roster?: Creature[],
  state?: RunState | null,
): number {
  if (state && mission) {
    const xp = starweaveOnSuccess(state, mission, team)
    if (xp > 0) for (const creature of team) grantXp(creature, xp, state)
  }
  // ✨ Luminesce banks its charge before the consume pass clears it.
  for (const creature of team) {
    if (creature.skillPrimed && creatureOwnSkill(creature)?.id === 'luminesce') {
      creature.luminesceReady = true
    }
  }
  blackholeAfterResolve(team, mission)
  consumeCreatureSkills(team)
  spendWager(team, mission)
  secondWindCycle(team)
  staticChargeOnResolve(team, true)
  osmosisOnSuccess(team)
  cascadeAfterResolve(team)
  if (roster) { moonlitOnSuccess(team, roster, state); sporebankOnSuccess(roster, state) }
  spendRideAlongs(team)
  if (roster) evergrowthAbsorb(roster, state)
  if (!mission) return 0
  forgeheartOnSuccess(roster ?? team, mission)
  eliteKillGrowth(team, mission)
  burdenOfAgesOnSuccess(team, mission, roster)
  balanceOfAgesExpire(team)
  return prospectorGold(team)
}

/** 🌞 Sundial doubles a mission's XP and gold payouts. */
export function payoutMultiplier(mission: Mission): number {
  return mission.sundialBoosted ? 2 : 1
}

/** Node completion: 🌳 Rootstock pays in, then 🤑 Avarice reads the
 * hoard, then 🎭 Understudy copies. Order matters — Avarice must see
 * Rootstock's gold. Mutates state.gold. */
export function onNodeComplete(state: RunState | null | undefined) {
  if (!state) return
  const failedReturn = state.encounter?.returnTrips.filter(trip => trip.starweaveFailed)
    .sort((a, b) => a.remaining - b.remaining)[0]
  if (failedReturn) starweaveOnFailedReturn(state, failedReturn.creatureIds)
  eternalOnNodeComplete(state)
  reflectionOnNodeComplete(state.roster)
  resetCreatureSkills(state.roster)
  // 🏗️ v6.92: a new node is a new field - Robodude's station is left behind.
  for (const c of state.roster) if (c.stationedPoi !== undefined) { c.stationedPoi = undefined; c.stationedX = undefined; c.stationedY = undefined }
  eclipseBloomOnNodeComplete(state)
  moonlitOnNodeComplete(state.roster)
  // 🌱 Catch level-ups granted outside resolutions (rewards, Deep Roots).
  evergrowthAbsorb(state.roster, state)
  // 🌬️ Second Wind is per-node ("this node"); Static charges persist.
  for (const c of state.roster) {
    c.secondWindPrimed = false
    c.benchSeconds = 0
    c.refractStats = undefined
    c.giggleElement = undefined
    c.giggleBonus = undefined
    // 🔥🐻🌊 v4.66 once-per-node meters reset here.
    c.giggleSpent = false
    c.hibernalBanked = false
    c.tideStacks = 0
    c.reflectionPartners = undefined
    c.wispGhosts = 0
    c.featherweightUsed = false
    c.routineStats = undefined
    c.routinePassive = undefined
  }
  if (state.encounter) {
    state.encounter.tithePrimed = false
    state.encounter.bulwarkArmed = false
    state.encounter.delugeSeconds = 0
    state.encounter.goldHighWater = 0
    state.encounter.rekindleUsed = false
  }
  state.gold += rootstockOnNodeComplete(state.roster, state)
  avariceOnNodeComplete(state.roster, state.gold)
  understudyOnNodeComplete(state.roster)
  state.gold += buriedTreasureInterest(state.roster, state.gold)
}

/** 🪙 Buried Treasure (v5.16, per Javi — Sable Corsair's new passive,
 * built to play with Plunder): at every node's end the hoard GROWS —
 * +1 gold per 20/15/10 held (Apotheosis: per 5). Interest compounds
 * node over node, feeding the shops and a fatter Plunder alike; the
 * tension is spending now versus letting the chest swell. */
export function buriedTreasureInterest(roster: Creature[], gold: number): number {
  let interest = 0
  for (const { tier } of passiveHolders(roster, 'buried-treasure')) {
    interest += Math.floor(gold / tierValue(CREATURE_PASSIVE_VALUES.buriedTreasureRate, tier))
  }
  return interest
}


// ---------- 🎯 Creature on-use skills (v2.86) ----------

/** This creature's own skill definition, if it has one. */
export function creatureOwnSkill(creature: Creature) {
  return getCreatureSkillDef(getCreatureDef(creature.defId).skill)
}

/** Ready to fire: it has a skill, it is home, and it is off cooldown. */
export function canUseCreatureSkill(creature: Creature): boolean {
  const def = creatureOwnSkill(creature)
  if (!def) return false
  if (!creature.available || creature.benched) return false
  if (def.kind === 'primed' && creature.skillPrimed) return true
  return (creature.skillCooldown ?? 0) <= 0
}

/** Puts the skill on cooldown after a use. */
function spendCreatureSkill(creature: Creature) {
  const def = creatureOwnSkill(creature)
  if (def) creature.skillCooldown = def.cooldown
}

/**
 * 🌱 Graft: permanently move a point from its highest element to the
 * target's lowest. Returns false if there is nothing sensible to move
 * (its highest is already 0, or it would move a point onto itself).
 */
export function useGraft(creature: Creature, target: Creature): boolean {
  if (creature.id === target.id) return false
  const from = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
  if (creature.stats[from] <= 0) return false
  const to = ELEMENTS.reduce((a, b) => (target.stats[b] < target.stats[a] ? b : a))
  const points = CREATURE_SKILL_VALUES.graftPoints
  creature.stats[from] -= points
  target.stats[to] += points
  spendCreatureSkill(creature)
  return true
}

/**
 * 🌸 Bloom: heal the player AND mend one injury (v4.27, per Javi —
 * reversing v3.21's cure removal; the skill-vs-rarity review found the
 * legendary's button was uncommon-grade at 5 HP alone). The cure goes
 * to the roster's MOST injured creature — one stack, matching the
 * rest-stop cure economy. Returns the HP actually restored.
 */
export function useBloom(creature: Creature, state: RunState): number {
  const before = state.hp
  // v4.69 (per Javi): the bloom heals the mother's LEVEL in HP.
  state.hp = Math.min(state.maxHp, state.hp + creature.level)
  let worst: Creature | null = null
  for (const mate of state.roster) {
    if (injuryStacks(mate) > (worst ? injuryStacks(worst) : 0)) worst = mate
  }
  if (worst) {
    worst.injuries = injuryStacks(worst) - 1
    worst.injured = worst.injuries > 0
  }
  spendCreatureSkill(creature)
  return state.hp - before
}

/** Arms (or disarms) a 'primed' skill — Skim, Overheat. */
export function toggleCreatureSkill(creature: Creature): boolean {
  const def = creatureOwnSkill(creature)
  if (!def || def.kind !== 'primed') return false
  if (creature.skillPrimed) {
    creature.skillPrimed = false
    return true
  }
  if (!canUseCreatureSkill(creature)) return false
  creature.skillPrimed = true
  return true
}

/** 💧 Skim: does this team travel instantly? (Same shape as Trailblazer.) */
export function teamSkimsTravel(team: Creature[]): boolean {
  return team.some(c => c.skillPrimed && creatureOwnSkill(c)?.id === 'skim')
}

/** 🔥 Overheat: bonus 🔥 an armed carrier lends its mission. */
export function overheatBonus(creature: Creature): number {
  const def = creatureOwnSkill(creature)
  if (!def || def.id !== 'overheat' || !creature.skillPrimed) return 0
  return CREATURE_SKILL_VALUES.overheatFire
}

// ── 🎩 Mr. Khool (v6.88): the first homage kit, settled with Javi 2026-09-10 ──
/**
 * 🎲 The Wager (v7.79, per Javi: "30sec cooldown and success should be a flat amount of gold and XP rather than %.
 * That way a non-gold mission rewards some and also make it every 5% missing instead"): every 5% the LOCKED chance
 * sat below 100 is a step. A win pays a flat purse per step (wagerPurse) on ANY mission; a loss costs +10% damage
 * per step (wagerDamageMult). No dice, no bet: a roll forced to 100 (Decree, Blackhole, Iron Week) has no steps.
 * Armed like Skim; `mission.wagered` is stamped at dispatch so the bet survives a reload, and the armed flag still
 * reads before dispatch.
 */
export function wagerSteps(mission: Mission, team: readonly Creature[] = [], rolledChance?: number): number {
  const armed = mission.wagered
    || team.some(c => c.defId === 'mr-khool' && c.skillPrimed && creatureOwnSkill(c)?.id === 'the-wager')
  if (!armed || typeof mission.assignedChance !== 'number') return 0
  // No dice, no bet: `rolledChance` is passed only for a FORCED roll (Decree, Blackhole, Iron Week), and a roll forced to
  // 100 or to 0 (an Iron Week boss under full cover) has no steps. An honest 0% bet keeps its steps, as before.
  if (typeof rolledChance === 'number' && (rolledChance >= 100 || rolledChance <= 0)) return 0
  return Math.max(0, Math.floor((100 - mission.assignedChance) / CREATURE_SKILL_VALUES.wagerStepPct))
}

/** 🎲 v7.79: his bet is PLACED - he walked out on a mission stamped with it (planned into a trial or a boss piece
 * is not out yet). A placed bet cannot be called off (useRun) and the roster shows it locked. */
export function wagerPlaced(creature: Creature, state: RunState | null | undefined): boolean {
  if (!creature.skillPrimed || creatureOwnSkill(creature)?.id !== 'the-wager' || !creature.assignedMissionId) return false
  // OUT, not just planned: a reformed boss piece copies the stamp until its next dispatch decides it again.
  const out = state?.encounter?.missions.find(m => m.id === creature.assignedMissionId)
  return !!out && out.status !== 'waiting' && !!out.wagered
}

/** 🎲 A lost bet's damage multiplier: +10% per step (×2 at 50% locked, ×2.6 at 20%). */
export function wagerDamageMult(steps: number): number {
  return 1 + CREATURE_SKILL_VALUES.wagerDamagePerStep * Math.max(0, steps)
}

/** 🎲 A won bet's flat purse: gold for the wallet and XP for each teammate, at the act's rate (act 3's past it). */
export function wagerPurse(steps: number, act = 1): { gold: number; xp: number } {
  const rates = CREATURE_SKILL_VALUES.wagerGoldPerStep
  const i = Math.min(Math.max(Math.floor(act) || 1, 1), rates.length) - 1
  const n = Math.max(0, steps)
  return { gold: Math.round(n * rates[i]!), xp: Math.round(n * CREATURE_SKILL_VALUES.wagerXpPerStep[i]!) }
}

/** 🎲 v7.79: a stamped bet is spent at resolution even if the button was turned off mid-trip (the roster refuses
 * that tap now, but an older save may carry it) - the cooldown cannot be dodged. */
function spendWager(team: Creature[], mission?: Mission) {
  if (!mission?.wagered) return
  for (const creature of team) {
    if (creatureOwnSkill(creature)?.id !== 'the-wager') continue
    creature.skillPrimed = false
    if ((creature.skillCooldown ?? 0) <= 0) spendCreatureSkill(creature)
  }
}

function khoolComposure(team: readonly Creature[]) {
  // 🎩 v7.77 (Stoneguard review): "benched" means creature.benched - NOT available:false, which is every
  // dispatched creature at resolution. The old test made that swap, so Composure never fired in live play.
  const khool = team.find(c => c.defId === 'mr-khool' && !c.benched)
  const passive = khool ? creaturePassive(khool) : null
  return passive && passive.def.id === 'composure' ? passive : null
}

/** 🎩 Composure: how many times a failed roll on his mission is rolled again (tiers 1 / 8 / 15). */
export function composureRerolls(team: readonly Creature[]): number {
  const passive = khoolComposure(team)
  return passive ? Math.min(3, passive.tier) : 0
}

/** 🎩 COOL HEAD (Apotheosis, Lv 17): a mission that still fails after every reroll deals half damage. */
export function composureApotheosis(team: readonly Creature[]): boolean {
  return (khoolComposure(team)?.tier ?? 0) >= 4
}

/**
 * Rolls the failed mission again, up to Composure's count, at the same chance
 * the wheel used. True when a reroll lands (the mission is stamped so the
 * resolution can say so). `rng` is injectable for the tests.
 */
export function composureRescue(state: RunState | null | undefined, mission: Mission, rng: () => number = Math.random): boolean {
  const team = mission.assignedCreatureIds
    .map(id => state?.roster.find(c => c.id === id))
    .filter((c): c is Creature => !!c)
  const rerolls = composureRerolls(team)
  if (!rerolls || mission.training) return false
  const chance = computeMissionOutcome(state, mission).chance
  for (let i = 0; i < rerolls; i++) {
    if (rng() * 100 < chance) { mission.composureSaved = true; return true }
  }
  return false
}

/** 🎩 v7.79: every d100 the wheel shows, in order - the first roll, then Composure's rerolls while it keeps missing
 * (it stops at the first landing). The LAST roll decides. A 0% chance has nothing to reroll for. The card plays one
 * spin per roll (utils/wheel.ts planWheel). */
export function composureRolls(chance: number, rerolls: number, d100: () => number = () => Math.random() * 100): number[] {
  const rolls = [d100()]
  if (chance <= 0) return rolls
  while (rolls.length <= rerolls && rolls[rolls.length - 1]! >= chance) rolls.push(d100())
  return rolls
}

// ── 🧤 Aid (v6.89): the valet, Mr. Khool's other half ──────────────────────
/**
 * Re-labels a statline to a mission's demand: the creature's best `count`
 * elements are read as the mission's `count` most-demanded elements, best
 * to most demanded. Totals never change; elements the mission does not
 * ask for keep their own label. Exported for the tests.
 */
export function relabelStats(stats: Stats, required: Stats, count: number): void {
  const demand = ELEMENTS.filter(e => required[e] > 0).sort((a, b) => required[b] - required[a])
  const own = ELEMENTS.filter(e => stats[e] > 0).sort((a, b) => stats[b] - stats[a])
  const n = Math.min(count, demand.length, own.length)
  if (n <= 0) return
  const moved = own.slice(0, n).map(e => stats[e])
  for (const e of own.slice(0, n)) stats[e] = 0
  demand.slice(0, n).forEach((e, i) => { stats[e] += moved[i]! })
}

/** 🧤 Anticipation, per creature inside the team sum (tiers: top one / top two / the whole statline; Apotheosis: Mr. Khool's too). */
export function anticipationRelabel(creature: Creature, team: readonly Creature[], mission: Mission, stats: Stats): void {
  const aid = team.find(c => c.defId === 'aid')
  if (!aid) return
  const tier = creaturePassiveTier(aid, 'anticipation')
  if (tier <= 0) return
  if (creature.id === aid.id) relabelStats(stats, mission.required, tier === 1 ? 1 : tier === 2 ? 2 : ELEMENTS.length)
  else if (creature.defId === 'mr-khool' && isApotheosis('anticipation', tier)) relabelStats(stats, mission.required, ELEMENTS.length)
}

/**
 * 🚃 The Carriage: every mission out in the field (walking there or
 * fighting) resolves now at the chance it locked at dispatch, and every
 * team is home at once - the tick does the resolving, one verdict each.
 * Returns how many it hurried; the skill is only spent when it moved someone.
 */
export function useCarriage(creature: Creature, state: RunState): number {
  const enc = state.encounter
  if (!enc) return 0
  let hurried = 0
  for (const mission of enc.missions) {
    if (mission.status !== 'traveling' && mission.status !== 'assigned') continue
    mission.travelRemaining = 0
    mission.fightTimer = 0
    mission.homeTravel = 0
    hurried++
  }
  if (hurried > 0) spendCreatureSkill(creature)
  return hurried
}

// ── 🕊️ The Noble One (v6.90): The Long Silence and Let Go ─────────────────
export const SILENCE_SECONDS = 10

/** Stack cap by tier (5 / 10 / 20); 0 while the sage is absent or dormant. */
export function silenceCap(state: RunState | null | undefined): number {
  const sage = state?.roster.find(c => c.defId === 'the-noble-one')
  if (!sage) return 0
  const tier = creaturePassiveTier(sage, 'the-long-silence')
  return tier <= 0 ? 0 : tier === 1 ? 5 : tier === 2 ? 10 : 20
}

/** Quiet seconds bank Silence: one stack per 10 s, capped by tier. Lives on the encounter, so a new node starts silent again. */
export function silenceTick(state: RunState, dt: number): void {
  const enc = state.encounter
  if (!enc || dt <= 0) return
  const cap = silenceCap(state)
  if (!cap) return
  enc.silenceClock = (enc.silenceClock ?? 0) + dt
  while (enc.silenceClock >= SILENCE_SECONDS) {
    enc.silenceClock -= SILENCE_SECONDS
    enc.silenceStacks = Math.min(cap, (enc.silenceStacks ?? 0) + 1)
  }
}

/** Any press - a surge armed, an on-use fired - clears the whole stack. THE FIRST WORD (Apotheosis) spares the first press each node. */
export function breakSilence(state: RunState | null | undefined): void {
  const enc = state?.encounter
  if (!enc) return
  const sage = state!.roster.find(c => c.defId === 'the-noble-one')
  if (sage && isApotheosis('the-long-silence', creaturePassiveTier(sage, 'the-long-silence')) && !enc.silenceSpared) {
    enc.silenceSpared = true
    return
  }
  enc.silenceStacks = 0
  enc.silenceClock = 0
}

/** +1 to every element on everyone per stack, while the sage is on the roster (rides the all-element chip in effectiveStats). */
export function silenceBonus(run: RunState | null | undefined): number {
  const stacks = run?.encounter?.silenceStacks ?? 0
  return stacks > 0 && !!run?.roster.some(c => c.defId === 'the-noble-one') ? stacks : 0
}

/**
 * 🕊️ Let Go: every stack becomes a permanent point, dealt around the
 * roster in order, each onto that creature's lowest element above zero.
 * Returns the points dealt; spent only when there was something to spend.
 */
export function useLetGo(creature: Creature, state: RunState): number {
  const enc = state.encounter
  const stacks = enc?.silenceStacks ?? 0
  if (!enc || stacks <= 0 || state.roster.length === 0) return 0
  for (let i = 0; i < stacks; i++) {
    const target = state.roster[i % state.roster.length]!
    const lowest = ELEMENTS.filter(e => target.stats[e] > 0).sort((a, b) => target.stats[a] - target.stats[b])[0] ?? ELEMENTS[0]!
    target.stats[lowest]++
  }
  enc.silenceStacks = 0
  enc.silenceClock = 0
  spendCreatureSkill(creature)
  return stacks
}

// ── 🌌 Lanfeust (v6.91): Gravity and Blackhole ─────────────────────────────
/** Seconds between pulls by tier (15 / 10 / 5); 0 while he is absent, dormant, or away without the Apotheosis. */
export function gravityInterval(state: RunState | null | undefined): number {
  const dragon = state?.roster.find(c => c.defId === 'lanfeust')
  if (!dragon) return 0
  const tier = creaturePassiveTier(dragon, 'gravity')
  if (tier <= 0) return 0
  const home = dragon.available && !dragon.assignedMissionId
  if (!home && !isApotheosis('gravity', tier)) return 0
  return tier === 1 ? 15 : tier === 2 ? 10 : 5
}

/** One pull: every waiting mission loses 1 point from its highest requirement (bosses and classes stand firm). Returns how many bent. */
export function gravityPull(state: RunState): number {
  let bent = 0
  for (const mission of state.encounter?.missions ?? []) {
    if (mission.status !== 'waiting' || mission.training || mission.tier === 'boss') continue
    const top = ELEMENTS.filter(e => mission.required[e] > 0).sort((a, b) => mission.required[b] - mission.required[a])[0]
    if (!top) continue
    mission.required[top]--
    bent++
  }
  return bent
}

/** The board bends toward him while he waits: the clock only runs while a pull is possible. */
export function gravityTick(state: RunState, dt: number): void {
  const enc = state.encounter
  if (!enc || dt <= 0) return
  const interval = gravityInterval(state)
  if (!interval) return
  enc.gravityClock = (enc.gravityClock ?? 0) + dt
  while (enc.gravityClock >= interval) {
    enc.gravityClock -= interval
    gravityPull(state)
  }
}

/** 🌀 An armed Blackhole aboard an eligible dispatch (Decree's eligibility: waiting, non-boss, non-gold, non-training). */
export function blackholeArmed(team: readonly Creature[], mission: Mission): boolean {
  return decreeEligible(mission)
    && team.some(c => c.defId === 'lanfeust' && c.skillPrimed && creatureOwnSkill(c)?.id === 'blackhole')
}

/** At resolution: a swallowed mission hands him its requirements; his next deployment spends them. */
export function blackholeAfterResolve(team: readonly Creature[], mission?: Mission): void {
  for (const creature of team) {
    if (creature.defId !== 'lanfeust') continue
    if (mission?.eaten) creature.blackholeStats = { ...mission.required }
    else if (creature.blackholeStats) creature.blackholeStats = undefined
  }
}

// ── 🏗️ Robodude (v6.92): Hold Position, Overkill and Fire Support ──────────
/** Does this creature hold its position after a mission (Siege Engine awake)? */
export function holdsPosition(creature: Creature): boolean {
  return creature.defId === 'robodude' && creaturePassiveTier(creature, 'siege-engine') > 0
}

/** He stays standing where the mission was: dispatchable from there, never in the home ring, and the site stays his. */
export function stationCreature(creature: Creature, mission: Pick<Mission, 'poi' | 'x' | 'y'>): void {
  creature.stationedPoi = mission.poi
  creature.stationedX = mission.x
  creature.stationedY = mission.y
  creature.available = true
  creature.assignedMissionId = null
}

/** Coverage without the cap: how far past (or short of) the requirement the team lands, on the required elements. */
export function coverageRaw(required: Stats, team: Stats): number {
  const total = totalRequired(required)
  if (total <= 0) return 1
  let landed = 0
  for (const element of ELEMENTS) if (required[element] > 0) landed += team[element]
  return landed / total
}

/** Overkill's threshold by tier (150 / 130 / 110 %); 0 without him. */
export function overkillThreshold(team: readonly Creature[]): number {
  const engine = team.find(c => c.defId === 'robodude')
  const tier = engine ? creaturePassiveTier(engine, 'siege-engine') : 0
  return tier <= 0 ? 0 : tier === 1 ? 150 : tier === 2 ? 130 : 110
}

/** Permanent points a Siege Engine Overkill pays him: 1, or 2 at SIEGE WORKS. */
export function siegeOverkillPoints(team: readonly Creature[]): number {
  const engine = team.find(c => c.defId === 'robodude')
  return engine && isApotheosis('siege-engine', creaturePassiveTier(engine, 'siege-engine')) ? 2 : 1
}

export function overkillTriggered(mission: Mission, team: readonly Creature[], stats: Stats): boolean {
  const threshold = overkillThreshold(team)
  return threshold > 0 && coverageRaw(mission.required, stats) * 100 >= threshold
}

/**
 * 🎯 Fire Support: from his station, unassigned, the live mission closest
 * to him counts his full statline without a slot until it resolves.
 * Returns the supported mission, or null (nothing spent).
 */
export function useFireSupport(creature: Creature, state: RunState): Mission | null {
  if (creature.stationedPoi === undefined || creature.stationedX === undefined || creature.stationedY === undefined) return null
  if (!creature.available || creature.assignedMissionId) return null
  const live = (state.encounter?.missions ?? []).filter(m =>
    (m.status === 'traveling' || m.status === 'assigned') && !m.fireSupportId && !m.assignedCreatureIds.includes(creature.id))
  if (!live.length) return null
  const closest = live.reduce((best, m) =>
    Math.hypot(m.x - creature.stationedX!, m.y - creature.stationedY!) < Math.hypot(best.x - creature.stationedX!, best.y - creature.stationedY!) ? m : best)
  closest.fireSupportId = creature.id
  spendCreatureSkill(creature)
  return closest
}

/** Consumes armed creature skills when their mission resolves. */
export function consumeCreatureSkills(team: Creature[]) {
  for (const creature of team) {
    if (!creature.skillPrimed) continue
    const def = creatureOwnSkill(creature)
    if (!def) continue
    creature.skillPrimed = false
    spendCreatureSkill(creature)
    // 🔥 Overheat trades the rest of the node for one huge mission.
    if (def.id === 'overheat') creature.benched = true
    // 🛡️ Bulwark spends the Sentinel for its (Weathered-set) lockout.
    if (def.id === 'bulwark') creature.benchSeconds = bulwarkLockoutFor(creature)
  }
}

/** Ticks creature-skill cooldowns. ONCE_PER_NODE values never tick down;
 * they are cleared by resetCreatureSkills at the node boundary. */
export function tickCreatureSkills(roster: Creature[], dt: number) {
  for (const creature of roster) {
    const cd = creature.skillCooldown ?? 0
    if (cd > 0 && cd < ONCE_PER_NODE) creature.skillCooldown = Math.max(0, cd - dt)
    // 🛡️ A spent Sentinel recovers on its own clock (v3.00).
    if ((creature.benchSeconds ?? 0) > 0) creature.benchSeconds = Math.max(0, (creature.benchSeconds ?? 0) - dt)
    // 🌊 Tidemother T3+: her surge cooldown is clamped down (v3.03).
    tideDelugeCooldownClamp(creature)
    // 🔋 Static Charge T2+ (v3.07): the banked cut fires once the
    // cooldown is actually ticking.
    if ((creature.staticCooldownCut ?? 0) > 0 && creature.surgeCooldown > 0 && creature.surgeCooldown < 9000) {
      creature.surgeCooldown = Math.max(0, creature.surgeCooldown - creature.staticCooldownCut!)
      creature.staticCooldownCut = 0
    }
    // 🪶 Understudy grants its random skills the moment a tier opens.
    understudyEnsureSkills(creature, Math.random, roster)
  }
}

/** Node boundary: once-per-node skills recharge and benches clear. */
export function resetCreatureSkills(roster: Creature[]) {
  for (const creature of roster) {
    creature.skillCooldown = 0
    creature.skillPrimed = false
    creature.benched = false
    creature.decreeUses = 0
    // 🧊 v5.21: the Warden's freeze pool refills each node.
    creature.permafrostUsed = 0
  }
}

/**
 * 🤖 Bot policy for creature skills (v2.86).
 *
 * The simulations must actually USE these, or the whole tranche is
 * unmeasured — the same trap the lifecycle payouts fell into before
 * v2.85. Deliberately simple and a little conservative: this is a FLOOR
 * on what these skills are worth, not an optimal line.
 */
export function botUseCreatureSkills(state: RunState) {
  for (const creature of state.roster) {
    const def = creatureOwnSkill(creature)
    if (!def || !canUseCreatureSkill(creature)) continue
    if (def.kind === 'primed') {
      // Arms Skim and Overheat whenever ready. Overheat benches the
      // carrier afterwards, which is the trade being measured.
      creature.skillPrimed = true
      continue
    }
    if (def.id === 'bloom') {
      // Only when the heal would not be thrown away (level-scaled, v4.69).
      if (state.hp <= state.maxHp - creature.level) useBloom(creature, state)
      continue
    }
    if (def.id === 'the-carriage') {
      // 🚃 v6.89: a floor - hurry the field once at least two teams are out.
      const out = state.encounter?.missions.filter(m => m.status === 'traveling' || m.status === 'assigned').length ?? 0
      if (out >= 2) useCarriage(creature, state)
      continue
    }
    if (def.id === 'let-go') {
      // 🕊️ v6.90: a floor - cash the Silence once five stacks are banked.
      if ((state.encounter?.silenceStacks ?? 0) >= 5) useLetGo(creature, state)
      continue
    }
    if (def.id === 'fire-support') {
      // 🎯 v6.92: a floor - shell the nearest fight whenever he is parked and free.
      useFireSupport(creature, state)
      continue
    }
    if (def.id === 'graft') {
      // Feed the teammate with the weakest single element.
      const target = state.roster
        .filter(c => c.id !== creature.id)
        .sort((a, b) => Math.min(...ELEMENTS.map(e => a.stats[e])) - Math.min(...ELEMENTS.map(e => b.stats[e])))[0]
      if (target) useGraft(creature, target)
    }
  }
}


// ---------- 🌟 Rare passives, tranche 2a (v2.87) ----------

/** Is this an Elite or boss mission? Kilnheart and Heat Death both key
 * off it, so the definition lives in one place. */
export function isEliteOrBoss(mission: Mission): boolean {
  return mission.tier === 'boss' || mission.tier === 'hard'
}

/** The mission's dominant required element — what 🔨 Forgeheart reads. */
export function missionElement(mission: Mission): ElementKey | null {
  let best: ElementKey | null = null
  for (const element of ELEMENTS) {
    if (mission.required[element] > 0 && (!best || mission.required[element] > mission.required[best])) {
      best = element
    }
  }
  return best
}

/**
 * 🔨 Forgeheart: every 🔥 mission cleared ANYWHERE feeds it — it does
 * not have to be aboard. Apotheosis widens that to every mission.
 */
export function forgeheartOnSuccess(roster: Creature[], mission: Mission) {
  const fire = missionElement(mission) === 'fire'
  for (const { creature, tier } of passiveHolders(roster, 'forgeheart')) {
    if (!fire && !isApotheosis('forgeheart', tier)) continue
    creature.stats.fire += tierValue(CREATURE_PASSIVE_VALUES.forgeheart, tier)
  }
}

/**
 * ☄️ Heat Death and ⚒️ Kilnheart both bank permanent stats off Elite and
 * boss kills, so they share one pass. Only creatures that were ON the
 * mission count — they defeated it.
 */
export function eliteKillGrowth(team: Creature[], mission: Mission) {
  if (!isEliteOrBoss(mission)) return
  const actBoss = mission.tier === 'boss'
  for (const { creature, tier } of passiveHolders(team, 'heat-death')) {
    const gain = tierValue(CREATURE_PASSIVE_VALUES.heatDeath, tier)
      * (isApotheosis('heat-death', tier) && actBoss ? 2 : 1)
    for (const element of ELEMENTS) creature.stats[element] += gain
  }
  for (const { creature, tier } of passiveHolders(team, 'kilnheart')) {
    const gain = tierValue(CREATURE_PASSIVE_VALUES.kilnheart, tier)
    creature.stats.earth += gain
    // Tier 1 is earth only; fire joins from tier 2.
    if (tier >= 2) creature.stats.fire += gain
  }
}

/** ⚒️ Kilnheart: Elite/boss XP multiplier (tier 4+, TRIPLE at Apotheosis). */
export function kilnheartXpMult(creature: Creature, mission: Mission): number {
  const tier = creaturePassiveTier(creature, 'kilnheart')
  if (!tier || !isEliteOrBoss(mission)) return 1
  if (isApotheosis('kilnheart', tier)) return 3
  return tier >= 4 ? 2 : 1
}

/**
 * ⛓️ Soulbind: a failed mission's requirement becomes permanent stats.
 * Points are taken from the largest requirements first, so a partial
 * keep is always the most valuable slice.
 */
export function soulbindOnFailure(team: Creature[], mission: Mission) {
  for (const { creature, tier } of passiveHolders(team, 'soulbind')) {
    let budget = tierValue(CREATURE_PASSIVE_VALUES.soulbind, tier)
    const wanted = ELEMENTS
      .filter(e => mission.required[e] > 0)
      .sort((a, b) => mission.required[b] - mission.required[a])
    if (isApotheosis('soulbind', tier)) {
      for (const element of wanted) creature.stats[element] += mission.required[element]
      continue
    }
    // Round-robin down the requirement so a 3-point keep spreads.
    let guard = 0
    while (budget > 0 && wanted.length > 0 && guard++ < 50) {
      for (const element of wanted) {
        if (budget <= 0) break
        creature.stats[element]++
        budget--
      }
    }
  }
}

/** 💰 Prospector: extra gold for every mission it joins. */
export function prospectorGold(team: Creature[]): number {
  let gold = 0
  for (const { tier } of passiveHolders(team, 'prospector')) {
    gold += tierValue(CREATURE_PASSIVE_VALUES.prospector, tier)
  }
  return gold
}

/** 🩸 Last Stand: a huge boost while the player is badly hurt. */
export function lastStandBonus(creature: Creature, run?: RunState | null): number {
  const tier = creaturePassiveTier(creature, 'last-stand')
  if (!tier || !run || run.maxHp <= 0) return 0
  const threshold = tierValue(CREATURE_PASSIVE_VALUES.lastStandThreshold, tier)
  if (run.hp / run.maxHp >= threshold) return 0
  return tierValue(CREATURE_PASSIVE_VALUES.lastStand, tier)
}

/** 😴 Power Nap: a full uninterrupted nap boosts its next trip. */
export function powerNapBonus(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'power-nap')
  if (!tier) return 0
  if ((creature.restSeconds ?? 0) < CREATURE_PASSIVE_VALUES.powerNapSeconds) return 0
  return tierValue(CREATURE_PASSIVE_VALUES.powerNap, tier)
}

/** 🎤 Crowd Work: chance per creature watching from home. */
export function crowdWorkBonus(team: Creature[], run?: RunState | null): number {
  if (!run) return 0
  let bonus = 0
  for (const { creature, tier } of passiveHolders(team, 'crowd-work')) {
    const watchers = run.roster.filter(c =>
      c.id !== creature.id && (isApotheosis('crowd-work', tier) || c.available)).length
    bonus += tierValue(CREATURE_PASSIVE_VALUES.crowdWork, tier) * watchers
  }
  return bonus
}

/**
 * 🐣 Underdog and 🪞 Reflection both need the TEAM, so they are applied
 * in missionTeamStats rather than effectiveStats. Returns the bonus this
 * creature gets from standing beside these particular teammates.
 */
export function teamContextBonus(creature: Creature, team: Creature[]): Stats {
  const out = emptyStats()
  const underdog = creaturePassiveTier(creature, 'underdog')
  if (underdog) {
    const lowest = Math.min(...team.map(c => c.level))
    if (creature.level <= lowest || isApotheosis('underdog', underdog)) {
      const gain = tierValue(CREATURE_PASSIVE_VALUES.underdog, underdog)
      for (const element of ELEMENTS) out[element] += gain
    }
  }
  const reflection = creaturePassiveTier(creature, 'reflection')
  if (reflection) {
    // v4.66 (per Javi): ALL elements — for each, the best value among
    // its TEAMMATES (not itself). v4.23: the copy always rounds UP — a
    // 25% share of 5 pays 2, never 1.
    const share = tierValue(CREATURE_PASSIVE_VALUES.reflection, reflection)
    for (const element of ELEMENTS) {
      let best = 0
      for (const mate of team) {
        if (mate.id === creature.id) continue
        if (mate.stats[element] > best) best = mate.stats[element]
      }
      if (best > 0) out[element] += Math.ceil(best * share)
    }
  }
  return out
}

/** 🐍 Gilded Scales: the shop discount this roster carries (0–1). */
export function gildedScalesDiscount(roster: Creature[]): number {
  let best = 0
  for (const { tier } of passiveHolders(roster, 'gilded-scales')) {
    best = Math.max(best, tierValue(CREATURE_PASSIVE_VALUES.gildedScales, tier))
  }
  return best
}

/** 🌿 Green Thumb: extra HP from rest stops (and Apotheosis cures all). */
/** 🌿 v4.66 (per Javi): ARRIVING at a rest stop permanently grows max
 * HP while a Green Thumb rides the roster — +2 per holder, +4 at the
 * Apotheosis. Called once at rest-node entry. */
export function greenThumbArrive(state: RunState) {
  for (const { tier } of passiveHolders(state.roster, 'green-thumb')) {
    state.maxHp += isApotheosis('green-thumb', tier) ? 4 : 2
  }
}

export function greenThumbHeal(roster: Creature[]): { heal: number; cureAll: boolean } {
  let heal = 0
  let cureAll = false
  for (const { tier } of passiveHolders(roster, 'green-thumb')) {
    heal += tierValue(CREATURE_PASSIVE_VALUES.greenThumb, tier)
    if (isApotheosis('green-thumb', tier)) cureAll = true
  }
  return { heal, cureAll }
}


// ---------- 🌟 Rare passives, tranche 2b (v2.91) ----------

/**
 * 🌬️ Second Wind: called when a creature's mission resolves (win or
 * lose). If its bonus was riding this mission, it is spent; otherwise
 * coming home primes the next dispatch. Apotheosis is always primed.
 */
export function secondWindCycle(team: Creature[]) {
  for (const creature of team) {
    const tier = creaturePassiveTier(creature, 'second-wind')
    if (!tier) continue
    if (isApotheosis('second-wind', tier)) { creature.secondWindPrimed = true; continue }
    creature.secondWindPrimed = !creature.secondWindPrimed
  }
}

/** The all-element bonus a primed Second Wind adds (rides effectiveStats). */
export function secondWindBonus(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'second-wind')
  if (!tier || !creature.secondWindPrimed) return 0
  return tierValue(CREATURE_PASSIVE_VALUES.secondWind, tier)
}

/**
 * 🔋 Static Charge: completed missions bank charges; while its surge is
 * armed every charge adds to ALL elements, and the surge spends them
 * (Apotheosis keeps them). Bank/spend both live in the shared
 * resolution pass so the sims inherit them.
 */
export function staticChargeBonus(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'static-charge')
  if (!tier || !creature.surgePrimed) return 0
  return (creature.staticCharges ?? 0) * tierValue(CREATURE_PASSIVE_VALUES.staticChargeWorth, tier)
}

export function staticChargeOnResolve(team: Creature[], success: boolean) {
  for (const creature of team) {
    const tier = creaturePassiveTier(creature, 'static-charge')
    if (!tier) continue
    // Spend first (this mission consumed the armed surge), then bank.
    if (creature.surgePrimed && !isApotheosis('static-charge', tier)) {
      // T2+: spending also cuts that surge's cooldown, 5s per charge —
      // banked here, applied in the tick once the cooldown exists.
      if (tier >= 2 && (creature.staticCharges ?? 0) > 0) {
        creature.staticCooldownCut = 5 * (creature.staticCharges ?? 0)
      }
      creature.staticCharges = 0
    }
    if (success) creature.staticCharges = (creature.staticCharges ?? 0) + 1
  }
}

/**
 * 🌱 Evergrowth: Rootwardens absorb every teammate level-up queued by
 * grantXp. Called at resolutions and node completion, so nothing is
 * lost whichever path granted the XP. Its OWN level-ups never feed it.
 */
export function evergrowthAbsorb(roster: Creature[], state?: RunState | null) {
  const wardens = passiveHolders(roster, 'evergrowth')
  // 🍄 Sporebank drinks from the same queue BEFORE it drains (v2.96):
  // +5 XP per teammate level-up; at tier 3 its own level-ups feed the
  // team (+10 XP, or a full LEVEL at Apotheosis). Snapshot first so the
  // cascade (XP → level-ups → more XP) settles next pass, not forever.
  const snapshot = roster.map(c => [c, c.levelUpsUncounted ?? 0] as const)
  for (const { creature: cap, tier } of passiveHolders(roster, 'sporebank')) {
    for (const [other, ups] of snapshot) {
      if (ups <= 0) continue
      if (other.id !== cap.id) {
        grantXp(cap, CREATURE_PASSIVE_VALUES.sporebankTeammateXp * ups, state)
      } else if (tier >= 3) {
        for (const mate of roster) {
          if (mate.id === cap.id) continue
          // 🍄 v4.88 (the Understudy loop audit): an apo Sporebank must
          // NOT gift full levels to another apo Sporebank — each gift
          // queues a level-up that gifts one back, a perpetual free
          // level every absorb pass. Mushrooms don't feed mushrooms:
          // a fellow apo cap takes the tier-3 XP drip instead.
          const mateApo = isApotheosis('sporebank', creaturePassiveTier(mate, 'sporebank'))
          if (isApotheosis('sporebank', tier) && !mateApo) grantXp(mate, TUNING.xp.toNextLevel(mate.level), state)
          else grantXp(mate, CREATURE_PASSIVE_VALUES.sporebankTeamXp * ups, state)
        }
      }
    }
  }
  if (wardens.length === 0) {
    for (const c of roster) c.levelUpsUncounted = 0
    return
  }
  for (const creature of roster) {
    const ups = creature.levelUpsUncounted ?? 0
    if (ups <= 0) continue
    creature.levelUpsUncounted = 0
    for (const { creature: warden, tier } of wardens) {
      if (warden.id === creature.id) continue
      warden.stats.grass += tierValue(CREATURE_PASSIVE_VALUES.evergrowth, tier) * ups
      if (isApotheosis('evergrowth', tier)) {
        warden.evergrowthTicker = (warden.evergrowthTicker ?? 0) + ups
        while (warden.evergrowthTicker >= 3) {
          warden.evergrowthTicker -= 3
          grantXp(warden, TUNING.xp.toNextLevel(warden.level), state)
        }
      }
    }
  }
}

/**
 * 🎶 Resonance: a Stormsinger out on a mission lifts every OTHER active
 * mission by tier% per creature aboard it. Apotheosis sings from home
 * too. `team` is the mission being priced, so its own singers are
 * excluded.
 */
export function resonanceBonus(team: Creature[], run?: RunState | null): number {
  if (!run) return 0
  let per = 0
  for (const { creature, tier } of passiveHolders(run.roster, 'resonance')) {
    if (team.some(c => c.id === creature.id)) continue
    const singing = !creature.available || isApotheosis('resonance', tier)
    if (singing) per += tierValue(CREATURE_PASSIVE_VALUES.resonance, tier)
  }
  return per * team.length
}

/** ⚓ Ballast: full-slot missions gain tier% per creature aboard. */
export function ballastBonus(team: Creature[], mission?: Mission): number {
  if (!mission || team.length < mission.slots) return 0
  let bonus = 0
  for (const { tier } of passiveHolders(team, 'ballast')) {
    bonus += tierValue(CREATURE_PASSIVE_VALUES.ballast, tier) * team.length
  }
  return bonus
}

/** ⚓ Ballast Apotheosis: the floor a full-slot mission cannot drop below. */
export function ballastFloor(team: Creature[], mission?: Mission): number {
  if (!mission || team.length < mission.slots) return 0
  for (const { tier } of passiveHolders(team, 'ballast')) {
    if (isApotheosis('ballast', tier)) return 50
  }
  return 0
}

/**
 * ☯️ Equanimity (REDESIGNED v5.11, per Javi — the old "elements at
 * exactly 0 count as covered" NEVER fired on Hollow Sage's own
 * 1/1/1/1/1 line): the Sage carries a pool of FLOATING points
 * (2/4/6 by tier, 12 at Apotheosis) that auto-assign to whatever the
 * assigned mission still LACKS after the whole team's contribution.
 * Gaps fill in element order until the pool runs dry — points are
 * never wasted on elements already covered, and an Eclipse-darkened
 * element is skipped (the boss skill keeps its word). Team-aware, so
 * it runs over the ASSEMBLED total inside missionTeamStats — the
 * preview %, the row chips and the resolver all read the same fill.
 */
export function equanimityFill(team: Creature[], mission: Mission, coverage: Stats): Stats {
  const out = emptyStats()
  let pool = 0
  for (const { tier } of passiveHolders(team, 'equanimity')) {
    pool += tierValue(CREATURE_PASSIVE_VALUES.equanimity, tier)
  }
  if (pool <= 0) return out
  for (const element of ELEMENTS) {
    if (pool <= 0) break
    if (mission.bossSkill === 'eclipse' && element === mission.eclipsedElement) continue
    const gap = mission.required[element] - coverage[element]
    if (gap <= 0) continue
    const spend = Math.min(gap, pool)
    out[element] = spend
    pool -= spend
  }
  return out
}


// ---------- 🌟 Rare tranche 2c (v2.92) ----------

/** 🪙 Tithe: pay gold now, boost the NEXT dispatch. */
export function useTithe(creature: Creature, state: RunState): boolean {
  if (!state.encounter || state.gold < CREATURE_SKILL_VALUES.titheCost) return false
  state.gold -= CREATURE_SKILL_VALUES.titheCost
  state.encounter.tithePrimed = true
  creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? 30
  return true
}

/** 🧱 Bulwark Drop: the next failure anywhere is absorbed. */
export function useBulwark(creature: Creature, state: RunState): boolean {
  if (!state.encounter) return false
  state.encounter.bulwarkArmed = true
  creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? ONCE_PER_NODE
  return true
}

/** 🔷 Refract: wear a teammate's statline until its next mission ends.
 * v4.10 (Javi's Barrow Knight report): the snapshot is the target's
 * EFFECTIVE line — triggered passives like Last Stand's +5-all ride
 * along — not just allocated points. effectiveStats skips its run-wide
 * layer for the wearer so the charms inside the snapshot count once. */
export function useRefract(creature: Creature, target: Creature, run?: RunState | null): boolean {
  if (creature.id === target.id) return false
  creature.refractStats = { ...effectiveStats(target, run) }
  creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? 30
  return true
}

/**
 * 🏮 Will-o'-Wisp: banked ghosts cover the mission's requirement,
 * largest elements first — the same virtual-stats channel as Equanimity,
 * so preview and resolution agree for free.
 */
export function wispVirtualStats(creature: Creature, mission: Mission): Stats {
  const out = emptyStats()
  const tier = creaturePassiveTier(creature, 'will-o-wisp')
  const ghosts = creature.wispGhosts ?? 0
  if (!tier || ghosts <= 0) return out
  let budget = ghosts * tierValue(CREATURE_PASSIVE_VALUES.willOWisp, tier)
  const wanted = ELEMENTS
    .filter(e => mission.required[e] > 0)
    .sort((a, b) => mission.required[b] - mission.required[a])
  for (const element of wanted) {
    if (budget <= 0) break
    const take = Math.min(budget, mission.required[element])
    out[element] = take
    budget -= take
  }
  return out
}

/** 🔥 The Catchfire spark riding a creature, applied in effectiveStats. */
export function giggleBonusFor(creature: Creature): { element: ElementKey; amount: number } | null {
  if (!creature.giggleElement || !(creature.giggleBonus ?? 0)) return null
  return { element: creature.giggleElement, amount: creature.giggleBonus! }
}


// ---------- 🌟 Rare tranche 2d (v2.93) ----------

/** 🪶 Featherweight: does this Sylph currently ride free? */
export function ridesFree(creature: Creature): boolean {
  const tier = creaturePassiveTier(creature, 'featherweight')
  if (!tier) return false
  return tier >= 2 || !creature.featherweightUsed
}

/** Slots a team actually occupies — free riders take none. */
export function slotsUsed(team: Creature[]): number {
  return team.filter(c => !ridesFree(c)).length
}

/** Capacity: the mission's slots, +1 per Apotheosis Sylph aboard. */
export function slotCapacity(mission: Mission, team: Creature[], run?: RunState | null): number {
  let cap = missionSlots(mission, run)
  // 🧤 v6.89 Aid's Anticipation: he joins any mission Mr. Khool is on
  // regardless of its slots - the pair always fits.
  if (team.some(c => c.defId === 'aid' && creaturePassiveTier(c, 'anticipation') > 0) && team.some(c => c.defId === 'mr-khool')) cap++
  for (const { tier } of passiveHolders(team, 'featherweight')) {
    if (isApotheosis('featherweight', tier)) cap++
  }
  // 🌵 Overgrowth (v2.95): it widens every mission it joins.
  for (const { tier } of passiveHolders(team, 'overgrowth')) {
    cap += tierValue(CREATURE_PASSIVE_VALUES.overgrowthSlots, tier)
  }
  // 📦 Quartermaster (v3.23 fix, per Javi): +1 slot on the mission it
  // is PUT ON — live at team-pick time, like Purpose Charm but carried.
  // The old dispatch-time version widened every FUTURE mission instead.
  // v4.16 (duplicates teachable, per Javi: "I don't want stacking"):
  // two quartermasters on one team still open ONE extra slot.
  cap += teamHasTrained(team, 'quartermaster') ? 1 : 0
  return cap
}

/**
 * 🎭 Routine: dispatched, the Mime silently copies whoever went out
 * right before it — worn until node end via effectiveStats (Refract,
 * being mission-scoped, wins while both are on).
 */
/**
 * 🎭 v4.26 (buddy's report via Javi: 89% at assignment, 75% on the
 * wheel): Routine re-copies its donor AT DISPATCH — after the preview
 * was scored — so a Mime's shown chance could silently change between
 * the assignment card and the spinner. This predicts the same copy on
 * CLONES, so previews score the line the wheel will actually spin.
 * Must mirror routineOnDispatch below exactly: every mime copies the
 * PRE-dispatch lastDispatchedId donor (self excluded).
 */
export function withPredictedRoutine(team: Creature[], state: RunState | null | undefined): Creature[] {
  const enc = state?.encounter
  // v4.68: the copy is fixed at the node's FIRST dispatch — once that
  // has happened, every mime's routineStats is already worn; nothing to
  // predict. Before it, a mime aboard THIS team will copy its opener.
  if (!enc || enc.firstDispatchDone) return team
  const model = team[0]
  if (!model) return team
  return team.map((creature) => {
    const tier = creaturePassiveTier(creature, 'routine')
    if (!tier || creature.id === model.id) return creature
    const share = tierValue(CREATURE_PASSIVE_VALUES.routine, tier)
    const copy = emptyStats()
    for (const element of ELEMENTS) copy[element] = Math.floor(model.stats[element] * share)
    // 🎭 v7.18 (follow-up review): the Apotheosis borrows the opener's
    // PASSIVE too (routineOnDispatch) - predicted here as well, or the
    // assigned odds miss what the resolution counts.
    return {
      ...creature,
      routineStats: copy,
      routinePassive: isApotheosis('routine', tier) ? getCreatureDef(model.defId).passive : creature.routinePassive,
    }
  })
}

/** 🎭 v4.68 (per Javi): the Mime studies the node's OPENING ACT — the
 * very first creature deployed — and wears its statline ON TOP of his
 * own for the rest of the node (it REPLACED his line before). Every
 * mime on the roster copies, home or aboard; the share ladders
 * 75/100/150% and the Apotheosis borrows the passive too. */
export function routineOnDispatch(state: RunState, team: Creature[]) {
  const enc = state.encounter
  if (!enc) return
  if (!enc.firstDispatchDone) {
    const model = team[0]
    if (model) {
      for (const mime of state.roster) {
        const tier = creaturePassiveTier(mime, 'routine')
        if (!tier || mime.id === model.id) continue
        const share = tierValue(CREATURE_PASSIVE_VALUES.routine, tier)
        const copy = emptyStats()
        for (const element of ELEMENTS) copy[element] = Math.floor(model.stats[element] * share)
        mime.routineStats = copy
        if (isApotheosis('routine', tier)) {
          mime.routinePassive = getCreatureDef(model.defId).passive
        }
      }
    }
  }
  const last = team[team.length - 1]
  if (last) enc.lastDispatchedId = last.id
}


// ---------- 💜 Epic tranche 3a (v2.94) ----------

/** ☀️ Zenith: extra ⭐ goal credit this team's success earns. */
export function zenithBonusCredit(team: Creature[]): number {
  let extra = 0
  for (const { tier } of passiveHolders(team, 'zenith')) {
    extra += tierValue(CREATURE_PASSIVE_VALUES.zenith, tier)
  }
  return extra
}

/**
 * 🧊 Permafrost — injury half (v4.67): while the Warden keeps watch
 * FROM HOME, injuries cannot land on the roster. v5.21 (per Javi):
 * home-only at EVERY tier now — the absorption half is what travels.
 * Runs BEFORE Moonlit Mending.
 */
export function permafrostPrevents(_team: Creature[], state?: RunState | null): boolean {
  if (!state || !injuriesActive(state)) return false
  return passiveHolders(state.roster, 'permafrost').some(({ creature }) => creature.available)
}

/**
 * 🧊 Permafrost — growth half (v5.20 conversion, v5.21 per-node pool,
 * both per Javi): chip damage from wins, losses and expiries FREEZES
 * into the Warden's body instead of the hearts — every absorbed point
 * becomes a permanent stat point, drawn from ONE pool per NODE of
 * 1/2/4 (apo 6) points per element. The pool fills as a waterfall —
 * 💧 to its cap, then 🪨, then 🌿 (a 5-chip at cap 2 pays +2/+2/+1) —
 * and once the node's pool is spent, further chips reach HP untouched,
 * so the freeze can't be farmed past its budget. Works even while the
 * Warden is deployed (v5.21); only the injury watch needs it home.
 * One Warden per event, no stacking. resetCreatureSkills refills the
 * pool with the rest of the per-node state.
 * Returns the damage that remains for the player.
 */
export function permafrostAbsorb(state: RunState, damage: number): number {
  if (damage <= 0) return damage
  for (const { creature, tier } of passiveHolders(state.roster, 'permafrost')) {
    const cap = tierValue(CREATURE_PASSIVE_VALUES.permafrostPool, tier)
    const used = creature.permafrostUsed ?? 0
    const absorbed = Math.min(damage, Math.max(0, cap * 3 - used))
    if (absorbed <= 0) return damage
    // Waterfall from the cumulative counter: 💧 spans [0,cap), 🪨
    // [cap,2cap), 🌿 [2cap,3cap) — the fill resumes where it left off.
    const band = (lo: number, hi: number) =>
      Math.max(0, Math.min(used + absorbed, hi) - Math.max(used, lo))
    creature.stats.water += band(0, cap)
    creature.stats.earth += band(cap, cap * 2)
    creature.stats.grass += band(cap * 2, cap * 3)
    creature.permafrostUsed = used + absorbed
    return damage - absorbed
  }
  return damage
}

/** ⚱️ Cremation Apotheosis: an Ossuary on the roster nulls expiry damage. */
export function cremationSparesDamage(roster: Creature[]): boolean {
  return passiveHolders(roster, 'cremation').some(({ tier }) => isApotheosis('cremation', tier))
}

/** 🏺 Reliquary: per-relic bonus, highest element (all five at Apotheosis). */
function reliquaryStats(creature: Creature, run?: RunState | null): Stats {
  const out = emptyStats()
  const tier = creaturePassiveTier(creature, 'reliquary')
  if (!tier || !run) return out
  const bonus = run.relics.length * tierValue(CREATURE_PASSIVE_VALUES.reliquary, tier)
  if (bonus <= 0) return out
  if (isApotheosis('reliquary', tier)) {
    for (const element of ELEMENTS) out[element] = bonus
  } else {
    // v4.66 (per Javi): highest AND lowest, not highest alone.
    // v5.08 (per Javi): lowest ABOVE 0 — a dead element stays dead.
    const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
    const live = ELEMENTS.filter(e => creature.stats[e] > 0)
    const low = live.length
      ? live.reduce((a, b) => (creature.stats[b] < creature.stats[a] ? b : a))
      : top
    out[top] = bonus
    if (low !== top) out[low] = bonus
  }
  return out
}

/** 💧 Osmosis: bank successes; every Nth pays its lowest AND highest
 * elements (v4.66, per Javi — the highest rider is baseline now). */
export function osmosisOnSuccess(team: Creature[]) {
  for (const { creature, tier } of passiveHolders(team, 'osmosis')) {
    const every = tierValue(CREATURE_PASSIVE_VALUES.osmosisEvery, tier)
    creature.osmosisCount = (creature.osmosisCount ?? 0) + 1
    if (creature.osmosisCount < every) continue
    creature.osmosisCount = 0
    const live = ELEMENTS.filter(e => creature.stats[e] > 0)
    const lowest = (live.length ? live : [...ELEMENTS])
      .reduce((a, b) => (creature.stats[b] < creature.stats[a] ? b : a))
    const highest = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
    creature.stats[lowest]++
    if (highest !== lowest) creature.stats[highest]++
  }
}

/**
 * 🌘 Eclipse Bloom: node completion feeds every element (bosses twice) —
 * on nodes she actually FOUGHT.
 *
 * Settled v3.68 (per Javi, final): fights, elites, bosses, puzzles and
 * ambushes pay; rests, chests, shops and peaceful mysteries do not. A
 * mystery that turns into an ambush or a puzzle has its node.type
 * rewritten on entry, so it counts as the fight it became.
 *
 * The set is DERIVED from TUNING.encounters.byNodeType — the same source
 * missiongen's isCombatNode uses — so a future combat node type joins
 * automatically instead of silently paying nothing.
 *
 * ⚠️ Measured cost, on the record: this scopes her to ~60% of a path's
 * nodes and the solo balance pass puts her at 9% win against a 12.5%
 * control (−3pp), versus +48pp when every node paid. Four node-set ×
 * ladder combinations were measured and every restricted one produced
 * the same −3pp — the lever is binary, not a dial. Shipped as Javi's
 * explicit call after seeing those numbers; see reports/balance-pass.md
 * and the saga note in docs/creature-expansion-proposal.md.
 */
/** 🌘 Which element blooms on the Empress's NEXT battle node — the
 * wheel turns one step per bloom, so the rotation is readable ahead of
 * time (v5.33 focus rebalance, per Javi's banked design). */
export function eclipseBloomElement(creature: Creature): ElementKey {
  return ELEMENTS[(creature.eclipseBloomIndex ?? 0) % ELEMENTS.length]!
}

/**
 * 🌘 Eclipse Bloom (REFOCUSED v5.33, per Javi's banked design): the
 * bloom pays ONE element — big — and rotates through the wheel node by
 * node instead of feeding all five. Bosses pay DOUBLE at every tier now
 * (was an Apotheosis-only perk), which is the timing game: route so the
 * bloom you want lands on the boss.
 */
export function eclipseBloomOnNodeComplete(state: RunState) {
  const nodeType = state.map?.nodes.find(n => n.id === state.activeNodeId)?.type
  if (!nodeType || !(nodeType in TUNING.encounters.byNodeType)) return
  const mult = nodeType === 'boss' ? 2 : 1
  for (const { creature, tier } of passiveHolders(state.roster, 'eclipse-bloom')) {
    const gain = tierValue(CREATURE_PASSIVE_VALUES.eclipseBloom, tier) * mult
    creature.stats[eclipseBloomElement(creature)] += gain
    creature.eclipseBloomIndex = ((creature.eclipseBloomIndex ?? 0) + 1) % ELEMENTS.length
  }
}


// ---------- 💜 Epic tranche 3b (v2.95) ----------

/** 🦉 Night Watch: multiplier on expiry-clock ticks (1 = no watch). */
export function nightWatchExpiryMult(roster: Creature[]): number {
  let slow = 0
  for (const { creature, tier } of passiveHolders(roster, 'night-watch')) {
    if (!creature.available && !isApotheosis('night-watch', tier)) continue
    slow = Math.max(slow, tierValue(CREATURE_PASSIVE_VALUES.nightWatch, tier))
  }
  return 1 - slow
}

/** 🗿 Bedrock Memory: unspent (and at Apo, spent) points as raw power. */
export function bedrockBonus(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'bedrock-memory')
  if (!tier) return 0
  const apo = isApotheosis('bedrock-memory', tier)
  const counted = Math.min(
    (creature.pendingStatPoints ?? 0) + (apo ? creature.spentStatPoints ?? 0 : 0),
    tierValue(CREATURE_PASSIVE_VALUES.bedrockCap, tier),
  )
  return counted * (apo ? 2 : 1)
}

/** 🐻 Hibernal Might (v4.66 redesign, per Javi tuned together): a full
 * unbroken rest BANKS permanent all-element stats — once per node, so
 * bench time can't print infinitely. Called from the encounter tick;
 * returns true when the bank pays (for tests). */
/**
 * 🐻 Hibernal Might (REFOCUSED v5.33, per Javi): the bank is EARTH-heavy
 * instead of +all — a fortress, not a rainbow. 30s of unbroken rest at
 * every tier now (was 60s until the Apotheosis).
 */
export function hibernalBank(creature: Creature): boolean {
  const tier = creaturePassiveTier(creature, 'hibernal-might')
  if (!tier || creature.hibernalBanked) return false
  if ((creature.restSeconds ?? 0) < tierValue(CREATURE_PASSIVE_VALUES.hibernalSeconds, tier)) return false
  creature.stats.earth += tierValue(CREATURE_PASSIVE_VALUES.hibernalEarth, tier)
  creature.stats.grass += tierValue(CREATURE_PASSIVE_VALUES.hibernalGrass, tier)
  creature.hibernalBanked = true
  return true
}

/** 🐻 Seconds until the hibernation banks (v4.67, for the roster's
 * countdown chip) — null when it doesn't apply or already paid. */
export function hibernalCountdown(creature: Creature): number | null {
  const tier = creaturePassiveTier(creature, 'hibernal-might')
  if (!tier || creature.hibernalBanked || !creature.available) return null
  const need = tierValue(CREATURE_PASSIVE_VALUES.hibernalSeconds, tier)
  const left = Math.ceil(need - (creature.restSeconds ?? 0))
  return left > 0 ? left : null
}

/** 🔮 Prismatic Refraction: extra charm copies + surge-per-charm. */
export function refractionExtras(creature: Creature, run?: RunState | null): { charmExtra: number; surgePerCharm: number } {
  const tier = creaturePassiveTier(creature, 'refraction2')
  if (!tier || !run) return { charmExtra: 0, surgePerCharm: 0 }
  return {
    charmExtra: tierValue(CREATURE_PASSIVE_VALUES.refractionMult, tier) - 1,
    surgePerCharm: tierValue(CREATURE_PASSIVE_VALUES.refractionSurge, tier),
  }
}

/** 🌵 Overgrowth: chance per body beyond the mission's base slots. */
export function overgrowthBonus(team: Creature[], mission?: Mission, run?: RunState | null): number {
  if (!mission) return 0
  let bonus = 0
  const extra = Math.max(0, team.length - missionSlots(mission, run))
  if (extra <= 0) return 0
  for (const { tier } of passiveHolders(team, 'overgrowth')) {
    bonus += tierValue(CREATURE_PASSIVE_VALUES.overgrowthPerBody, tier) * extra
  }
  return bonus
}


// ---------- 💜 Epic tranche 3c (v2.96) ----------

/**
 * 🌙 Moonlit Mending's injury guard, checked per would-be injury:
 * Apotheosis blocks the roster outright; tier 3 stops teammates from
 * STACKING (their first stack still lands).
 */
export function moonlitPrevents(victim: Creature, team: Creature[], state?: RunState | null): boolean {
  if (!state) return false
  for (const { creature, tier } of passiveHolders(state.roster, 'moonlit-mending')) {
    if (isApotheosis('moonlit-mending', tier)) return true
    if (tier >= 3 && team.some(c => c.id === creature.id) && injuryStacks(victim) >= 1) return true
  }
  return false
}

/** 🌙 Tier 2+ (v4.67, per Javi): each success draws from a 3-marble
 * bag — one winner, without replacement, refilled when empty — so it
 * procs EXACTLY once per three successes with random order ("bag of
 * marbles odds"). The winning marble cures a stack AND heals you the
 * bloom's LEVEL in HP. */
export function moonlitOnSuccess(team: Creature[], roster: Creature[], state?: RunState | null, roll: () => number = Math.random) {
  for (const { creature, tier } of passiveHolders(team, 'moonlit-mending')) {
    if (tier < 2) continue
    if (!creature.moonlitBag?.length) {
      // v4.69 (per Javi): 33% → 20% — one winner in a FIVE-marble bag.
      const bag = [true, false, false, false, false]
      for (let i = bag.length - 1; i > 0; i--) {
        const j = Math.floor(roll() * (i + 1))
        ;[bag[i], bag[j]] = [bag[j]!, bag[i]!]
      }
      creature.moonlitBag = bag
    }
    if (!creature.moonlitBag.shift()) continue
    const worst = [...roster].sort((a, b) => injuryStacks(b) - injuryStacks(a))[0]
    if (worst && injuryStacks(worst) > 0) cureCreatureStack(worst)
    if (state) state.hp = Math.min(state.maxHp, state.hp + creature.level)
  }
}

/** 🌙 Tier 1: node end cures one stack team-wide. */
export function moonlitOnNodeComplete(roster: Creature[]) {
  for (const { tier } of passiveHolders(roster, 'moonlit-mending')) {
    if (tier < 1) continue
    const worst = [...roster].sort((a, b) => injuryStacks(b) - injuryStacks(a))[0]
    if (worst && injuryStacks(worst) > 0) cureCreatureStack(worst)
  }
}

/** 🍄 Sporebank: +XP per mission cleared anywhere (tier 2+). */
export function sporebankOnSuccess(roster: Creature[], state?: RunState | null) {
  for (const { creature, tier } of passiveHolders(roster, 'sporebank')) {
    const xp = tierValue(CREATURE_PASSIVE_VALUES.sporebankMissionXp, tier)
    if (xp > 0) grantXp(creature, xp, state)
  }
}


// ---------- 💜 Epic tranche 3c part 2 (v2.97) ----------

/** 🦔 Thorn Toll (REDESIGNED v5.14, per Javi: "should be better for an
 * epic"): no more priming — the Quillhog's quills blanket the FIELD.
 * Every mission asks its tier amount (2/4/6, apo 12) less total
 * requirement while the hog is on the roster, spent against the
 * mission's largest elements first. Applied in missionTeamStats as
 * virtual coverage, so the preview % and resolver agree. */
export function thornTollVirtualStats(creature: Creature, mission: Mission): Stats {
  const out = emptyStats()
  const tier = creaturePassiveTier(creature, 'thorn-toll')
  if (!tier) return out
  let budget = tierValue(CREATURE_PASSIVE_VALUES.thornToll, tier)
  const wanted = ELEMENTS
    .filter(e => mission.required[e] > 0)
    .sort((a, b) => mission.required[b] - mission.required[a])
  for (const element of wanted) {
    if (budget <= 0) break
    const take = Math.min(budget, mission.required[element])
    out[element] = take
    budget -= take
  }
  return out
}

/** 🌈 Refraction Cascade: the team-wide share of the serpent's surge. */
export function cascadeTeamBonus(team: Creature[], run?: RunState | null): { elements: ElementKey[]; amount: number; own: number; carrierId: string } | null {
  for (const { creature, tier } of passiveHolders(team, 'refraction-cascade')) {
    if (!creature.surgePrimed) continue
    const skill = creatureSkill(creature, run)
    if (skill.kind === 'none' || skill.elements.length === 0) continue
    const mult = tierValue(CREATURE_PASSIVE_VALUES.cascadeMult, tier)
    // 🔋 v4.72: the Cell's doubling rides INTO the cascade — a doubled
    // surge cascades doubled (and Prism Surge cascades all five).
    const cell = surgeCellMult(skill, run)
    // Its own effectiveStats already carries cell× — the team addition
    // is mult× for every OTHER member, plus (mult−1)× for itself.
    // carrierId (v4.76): lets the assignment rows paint each member's
    // share as green chips without re-deriving who the serpent is.
    return { elements: skill.elements, amount: skill.bonus * cell * mult, own: skill.bonus * cell, carrierId: creature.id }
  }
  return null
}

/** 🌈 Apotheosis: the surge never cools down after resolving. */
export function cascadeAfterResolve(team: Creature[]) {
  for (const { creature, tier } of passiveHolders(team, 'refraction-cascade')) {
    if (isApotheosis('refraction-cascade', tier)) creature.surgeCooldown = 0
  }
}


// ---------- 💜 Epic tranche 3d part 1 (v2.98) ----------

/** 🏴‍☠️ Plunder: gold above the floor becomes permanent power.
 * v5.15 (per Javi: "Plunder still feels weak"): each converted point
 * pays the HIGHEST element AND the lowest above 0 — double value per
 * doubloon, same dead-elements-stay-dead rule as Martyr/Reliquary. */
export function usePlunder(creature: Creature, state: RunState): boolean {
  const excess = state.gold - CREATURE_SKILL_VALUES.plunderFloor
  const points = Math.floor(excess / CREATURE_SKILL_VALUES.plunderRate)
  if (points <= 0) return false
  state.gold -= points * CREATURE_SKILL_VALUES.plunderRate
  const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
  const live = ELEMENTS.filter(e => creature.stats[e] > 0)
  const low = live.length
    ? live.reduce((a, b) => (creature.stats[b] < creature.stats[a] ? b : a))
    : top
  creature.stats[top] += points
  if (low !== top) creature.stats[low] += points
  creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? ONCE_PER_NODE
  return true
}

/** 🌊 Deluge: opens the +25% window over the whole field. */
export function useDeluge(creature: Creature, state: RunState): boolean {
  if (!state.encounter) return false
  state.encounter.delugeSeconds = CREATURE_SKILL_VALUES.delugeSeconds
  creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? ONCE_PER_NODE
  return true
}

/** 🌊 The window's clock, ticked beside the skill cooldowns. */
export function tickEncounterBuffs(state: RunState | null | undefined, dt: number) {
  const enc = state?.encounter
  if (!enc) return
  if ((enc.delugeSeconds ?? 0) > 0) enc.delugeSeconds = Math.max(0, (enc.delugeSeconds ?? 0) - dt)
  // 🐲 Dragon's Hoard Apotheosis reads the node's PEAK gold (v3.03).
  if (state && state.gold > (enc.goldHighWater ?? 0)) enc.goldHighWater = state.gold
  // ♾️ Eternal Return takes its node-start snapshot on the first tick.
  if (state && enc.status === 'active' && enc.statSnapshot === undefined) eternalSnapshot(state)
}


/**
 * 🔭 Foresight (v2.99): rerolls every WAITING mission's requirement,
 * drawn element-by-element with the roster's own stat totals as the
 * weights (+1 smoothing so nothing is impossible). Totals never change —
 * the mission asks the same amount, just in shapes the team can answer.
 */
export function useForesight(creature: Creature, state: RunState, target?: Creature | null, rng: () => number = Math.random): boolean {
  const enc = state.encounter
  if (!enc) return false
  // v4.66 (per Javi: "change a node to match 1 creature"): the Oracle
  // reads ONE chosen creature now — every waiting mission rerolls into
  // that creature's shape (squared weights, +1 smoothing so nothing is
  // impossible). No target given (old saves mid-flow) falls back to the
  // roster read.
  const weights = emptyStats()
  if (target) {
    for (const element of ELEMENTS) weights[element] = target.stats[element]
  } else {
    for (const mate of state.roster) {
      for (const element of ELEMENTS) weights[element] += mate.stats[element]
    }
  }
  let any = false
  for (const mission of enc.missions) {
    if (mission.status !== 'waiting' || mission.tier === 'boss' || mission.goldReward) continue
    const total = ELEMENTS.reduce((n, e) => n + mission.required[e], 0)
    if (total <= 0) continue
    const fresh = emptyStats()
    for (let i = 0; i < total; i++) {
      // v4.27: SQUARED weights — the reroll leans HARD into the strong
      // elements. Totals never change.
      const sum = ELEMENTS.reduce((n, e) => n + weights[e] * weights[e] + 1, 0)
      let roll = rng() * sum
      for (const element of ELEMENTS) {
        roll -= weights[element] * weights[element] + 1
        if (roll <= 0) { fresh[element]++; break }
      }
    }
    mission.required = fresh
    any = true
  }
  if (any) creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? 60
  return any
}


// ---------- 🛡️ Bulwark + Weathered (v3.00, per Javi) ----------
// The first creature to carry BOTH an on-use skill and a passive — the
// passive exists to upgrade the skill. (The one-or-the-other rule was
// about elemental vs custom passives, never skill+passive.)

/** Is a live Bulwark riding this team? */
export function bulwarkAboard(team: Creature[]): boolean {
  return team.some(c => c.skillPrimed && creatureOwnSkill(c)?.id === 'bulwark')
}

/** The XP share a Bulwarked failure still pays (0 / 0.5 / 1). */
export function bulwarkXpShare(team: Creature[]): number {
  if (!bulwarkAboard(team)) return 1
  let share = 0
  for (const creature of team) {
    if (!(creature.skillPrimed && creatureOwnSkill(creature)?.id === 'bulwark')) continue
    const tier = creaturePassiveTier(creature, 'weathered')
    if (tier > 0) share = Math.max(share, tierValue(CREATURE_PASSIVE_VALUES.weatheredXpShare, tier))
  }
  return share
}

/** 🪨 Weathered sets the lockout its Bulwark costs. */
export function bulwarkLockoutFor(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'weathered')
  if (tier > 0) return tierValue(CREATURE_PASSIVE_VALUES.weatheredLockout, tier)
  return CREATURE_SKILL_VALUES.bulwarkLockout
}


// ---------- ✨ Luminesce + Bioluminance (v3.01) ----------
// The second dual-carrier: the passive widens WHERE the skill's bonus
// card applies, and its Apotheosis makes the arming unnecessary.

/** Node types the bonus card reaches at a given Bioluminance tier. */
function luminesceCovers(nodeType: string | undefined, tier: number): boolean {
  if (nodeType === 'chest' || nodeType === 'rest' || nodeType === 'mystery') return tier >= 1
  if (nodeType === 'elite' || nodeType === 'boss') return tier >= 2
  return true // ordinary combat nodes: the skill's own baseline
}

/**
 * Extra reward cards this pick offers. Consumes the armed charge when
 * it applies (Apotheosis never arms and never consumes).
 */
export function luminesceExtraCards(run: RunState, nodeType?: string): number {
  for (const { creature, tier } of passiveHolders(run.roster, 'bioluminance')) {
    if (isApotheosis('bioluminance', tier)) return 2
    if (creature.luminesceReady && luminesceCovers(nodeType, tier)) {
      creature.luminesceReady = false
      return 1
    }
  }
  // A Jelly below level 5 has the skill but no passive tier yet.
  for (const creature of run.roster) {
    if (creatureOwnSkill(creature)?.id === 'luminesce' && creature.luminesceReady
      && luminesceCovers(nodeType, 0)) {
      creature.luminesceReady = false
      return 1
    }
  }
  return 0
}


// ---------- ⚒️ Reforge (v3.02) ----------

/**
 * The Smith rewrites a TEAMMATE's allocation. The new spread must spend
 * exactly the points the old one held — Reforge moves power, it never
 * creates or destroys it. Returns false (and applies nothing) otherwise.
 */
export function useReforge(smith: Creature, target: Creature, newStats: Stats): boolean {
  if (smith.id === target.id) return false
  const oldTotal = ELEMENTS.reduce((n, e) => n + target.stats[e], 0)
  const newTotal = ELEMENTS.reduce((n, e) => n + newStats[e], 0)
  if (newTotal !== oldTotal) return false
  if (ELEMENTS.some(e => newStats[e] < 0 || !Number.isInteger(newStats[e]))) return false
  for (const element of ELEMENTS) target.stats[element] = newStats[element]
  smith.skillCooldown = creatureOwnSkill(smith)?.cooldown ?? ONCE_PER_NODE
  return true
}


// ---------- 🧡 Legendary tranche 4a (v3.03) ----------

/** 🐲 Dragon's Hoard: gold held IS power. */
export function dragonsHoardBonus(creature: Creature, run?: RunState | null): number {
  const tier = creaturePassiveTier(creature, 'dragons-hoard')
  if (!tier || !run) return 0
  const apo = isApotheosis('dragons-hoard', tier)
  const held = apo ? Math.max(run.gold, run.encounter?.goldHighWater ?? 0) : run.gold
  const raw = Math.floor(held / 25) * tierValue(CREATURE_PASSIVE_VALUES.hoardPer, tier)
  return Math.min(raw, tierValue(CREATURE_PASSIVE_VALUES.hoardCap, tier))
}

/** 🌊 Tidemother's Deluge: the whole team surges when she does.
 * v4.66 (per Javi, tuned together): her casts STACK for the node — a
 * spent surge leaves a lasting layer (tideStacks), and a primed cast
 * counts as one more. */
export function tideDelugeTeamBonus(team: Creature[], run?: RunState | null): { elements: ElementKey[]; amount: number; own: number; carrierId: string } | null {
  for (const { creature, tier } of passiveHolders(team, 'tide-deluge')) {
    const stacks = (creature.tideStacks ?? 0) + (creature.surgePrimed ? 1 : 0)
    if (stacks === 0) continue
    const skill = creatureSkill(creature, run)
    if (skill.kind === 'none' || skill.elements.length === 0) continue
    // 🔋 v4.72: the Cell doubles the SURGE half; the tier rider is the
    // passive's own addition and stays as written.
    const cell = surgeCellMult(skill, run)
    return {
      elements: skill.elements,
      amount: (skill.bonus * cell + tierValue(CREATURE_PASSIVE_VALUES.tideDelugeBonus, tier)) * stacks,
      own: creature.surgePrimed ? skill.bonus * cell : 0,
      carrierId: creature.id,
    }
  }
  return null
}

/** 🌊 A spent Tidemother surge becomes a lasting node layer (v4.66). */
export function bankTideStack(creature: Creature) {
  if (!creaturePassiveTier(creature, 'tide-deluge')) return
  creature.tideStacks = (creature.tideStacks ?? 0) + 1
}

/** 🌊 T3+: her cooldown is clamped down; Apotheosis removes it. Runs in
 * the tick, so it holds whatever order set the cooldown. */
export function tideDelugeCooldownClamp(creature: Creature) {
  const tier = creaturePassiveTier(creature, 'tide-deluge')
  if (!tier) return
  // v3.59: an absolute cap (10s at tier 3, 0 at Apotheosis) rather than
  // seconds shaved off the base, so the number in the tier text IS the
  // number the player waits.
  const cap = tierValue(CREATURE_PASSIVE_VALUES.tideDelugeCooldownCap, tier)
  if (!Number.isFinite(cap)) return
  if (creature.surgeCooldown > cap && creature.surgeCooldown < 9000) creature.surgeCooldown = cap
}

/**
 * 🌊 v3.59 (per Javi): the team-wide surge also carries Speed and Fight
 * into the mission it fires on. Derived from the team on the spot rather
 * than stamped on each creature, so it cannot leak into a later dispatch
 * or need a save field.
 */
export function tideDelugeRider(team: Creature[]): { speed: number; fight: number } {
  for (const { creature, tier } of passiveHolders(team, 'tide-deluge')) {
    if (!creature.surgePrimed) continue
    return {
      speed: tierValue(CREATURE_PASSIVE_VALUES.tideDelugeSpeed, tier),
      fight: tierValue(CREATURE_PASSIVE_VALUES.tideDelugeFight, tier),
    }
  }
  return { speed: 0, fight: 0 }
}

/**
 * 🔥 Rekindle: the player just hit 0 HP. Returns true if a Phoenix
 * catches it — HP restored (1, or full at Apotheosis), every injury
 * burned away, the save spent for the act (T1) or the node (T2+).
 */
export function applyRekindle(state: RunState): boolean {
  if (state.hp > 0) return false
  for (const { creature, tier } of passiveHolders(state.roster, 'rekindle')) {
    void creature
    // v3.59 (per Javi): once per NODE from tier 1 — the act-long lockout
    // is gone, so `rekindleActUsed` is now only read by old saves.
    if (state.encounter?.rekindleUsed) continue
    if (state.encounter) state.encounter.rekindleUsed = true
    state.hp = isApotheosis('rekindle', tier) ? state.maxHp : 1
    for (const mate of state.roster) healInjuriesFully(mate)
    // v3.59 tier 2: the fire that saves you also feeds the whole roster.
    const gain = tierValue(CREATURE_PASSIVE_VALUES.rekindleAllElements, tier)
    if (gain > 0) {
      for (const mate of state.roster) {
        for (const element of ELEMENTS) mate.stats[element] += gain
      }
    }
    return true
  }
  return false
}


// ---------- 🧡 Legendary tranche 4b (v3.04) ----------

/** 🌱 Millennia: what one of ITS level-ups costs and pays. */
export function millenniaPerLevel(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'millennia')
  if (!tier) return 0
  // v3.09 gated this to even levels only (it measured as the strongest
  // creature in the game at +39.2pp solo). v3.59 (per Javi) hands that
  // back: EVERY level pays, and the tier ladder stretched to 1/5/11/19
  // instead, so the big multipliers arrive much later in a run.
  return tierValue(CREATURE_PASSIVE_VALUES.millenniaPerLevel, tier)
}

export function millenniaXpCost(creature: Creature, base: number): number {
  const tier = creaturePassiveTier(creature, 'millennia')
  if (!tier) return base
  return Math.max(1, Math.ceil(base * tierValue(CREATURE_PASSIVE_VALUES.millenniaXpMult, tier)))
}

/** 🌱 T2+: each completed ACT feeds it. Also ♾️ Apo's act re-grant. */
export function onActComplete(state: RunState) {
  // 🪶 Understudy Apotheosis: one known skill re-rolls each act.
  for (const { creature, tier } of passiveHolders(state.roster, 'mirri-understudy')) {
    if (!isApotheosis('mirri-understudy', tier)) continue
    const known = creature.learnedSkills ?? []
    if (known.length === 0) continue
    const dropIndex = Math.floor(Math.random() * known.length)
    const pool = TRAINED_SKILL_IDS.filter(id => canLearnSkill(creature, id))
    if (pool.length === 0) continue
    const next = [...known]
    next[dropIndex] = pool[Math.floor(Math.random() * pool.length)]!
    creature.learnedSkills = next
  }
  // 🌱 v5.33: the act tick follows the level rule — top two only.
  for (const { creature, tier } of passiveHolders(state.roster, 'millennia')) {
    if (tier < 2) continue
    const ranked = [...ELEMENTS].sort((a, b) => creature.stats[b] - creature.stats[a])
    creature.stats[ranked[0]!]++
    if (ranked[1]) creature.stats[ranked[1]]++
  }
  // ♾️ v5.30 (per Javi): the Eternal Return act-end bonus is RETIRED —
  // any bank a pre-v5.30 save still carries is simply dropped.
  state.eternalActGains = undefined
}

/** ♾️ Node start: remember where everyone stood. */
export function eternalSnapshot(state: RunState) {
  if (!state.encounter) return
  if (passiveHolders(state.roster, 'eternal-return').length === 0) return
  const snap: Record<string, Stats> = {}
  for (const creature of state.roster) snap[creature.id] = { ...creature.stats }
  state.encounter.statSnapshot = snap
}

/** ♾️ Node end: the node's gains are granted again, ×tier. */
export function eternalOnNodeComplete(state: RunState) {
  const snap = state.encounter?.statSnapshot
  if (!snap) return
  let mult = 0
  for (const { tier } of passiveHolders(state.roster, 'eternal-return')) {
    mult = Math.max(mult, tierValue(CREATURE_PASSIVE_VALUES.eternalReturn, tier))
  }
  if (mult <= 0) return
  for (const creature of state.roster) {
    const before = snap[creature.id]
    if (!before) continue
    const gained = emptyStats()
    for (const element of ELEMENTS) gained[element] = Math.max(0, creature.stats[element] - before[element])
    for (const element of ELEMENTS) creature.stats[element] += gained[element] * mult
  }
  if (state.encounter) state.encounter.statSnapshot = undefined
}


// ---------- 🧡 Legendary tranche 4c (v3.05) ----------

/**
 * 🪶 Understudy: the crow knows random trained skills innately. Runs in
 * the tick, idempotent — grants only the shortfall, never re-rolls what
 * it already knows (except the Apotheosis act re-roll, handled in
 * onActComplete).
 */
/** 🎓 v4.70 (per Javi): the central can-this-creature-learn-this rule —
 * never the same skill twice, and Mirricrow never the trained skill of
 * its own name (Understudy-on-Understudy would loop the copy). Used by
 * the school, the training missions and the crow's innate rolls. */
export function canLearnSkill(creature: Creature, skillId: string): boolean {
  if ((creature.learnedSkills ?? []).includes(skillId)) return false
  if (skillId === 'understudy' && creature.defId === 'mirricrow') return false
  return true
}

export function understudyEnsureSkills(creature: Creature, rng: () => number = Math.random, teammates: Creature[] = []) {
  const tier = creaturePassiveTier(creature, 'mirri-understudy')
  if (!tier) return
  const want = tierValue(CREATURE_PASSIVE_VALUES.understudyKnown, tier)
  const granted = creature.understudyGranted ?? 0
  if (granted >= want) return
  const known = new Set(creature.learnedSkills ?? [])
  // v3.19 (per Javi's audit): prefer skills NOBODY on the roster knows —
  // the run-wide "never offered twice" rule — falling back to merely
  // self-unknown when the catalog is nearly exhausted.
  const rosterKnown = new Set([...known, ...teammates.flatMap(c => c.learnedSkills ?? [])])
  const fresh = TRAINED_SKILL_IDS.filter(id => !rosterKnown.has(id) && canLearnSkill(creature, id))
  const pool = fresh.length > 0 ? fresh : TRAINED_SKILL_IDS.filter(id => canLearnSkill(creature, id))
  for (let i = granted; i < want && pool.length > 0; i++) {
    const pick = pool.splice(Math.floor(rng() * pool.length), 1)[0]!
    creature.learnedSkills = [...(creature.learnedSkills ?? []), pick]
  }
  creature.understudyGranted = want
}

/** 🎭 Mimic: wear a CHOSEN teammate's Surge for one mission (v4.27,
 * per Javi's rarity review — the random pick read rare-grade on a
 * legendary). The legendary flourish: a Dual Surge donor is copied at
 * the FULL single-surge bonus on BOTH elements. */
export function useMimic(creature: Creature, state: RunState, target: Creature): boolean {
  if (target.id === creature.id) return false
  const skill = creatureSkill(target, state)
  if (skill.kind === 'none' || skill.elements.length === 0) return false
  creature.mimicElements = [...skill.elements]
  creature.mimicBonus = Math.max(skill.bonus, TUNING.skills.surgeBonus)
  creature.skillCooldown = creatureOwnSkill(creature)?.cooldown ?? ONCE_PER_NODE
  return true
}

/** 🌹 Bloodbloom: does a Lily make this team a pact-holder? */
export function bloodbloomAboard(team: Creature[]): boolean {
  return team.some(c => creaturePassiveTier(c, 'bloodbloom') >= 1)
}

/** The pact's HP cost for this team (2 without a Lily; ×team at T3). */
export function bloodPactCost(team: Creature[]): number {
  let best = -1
  for (const { tier } of passiveHolders(team, 'bloodbloom')) best = Math.max(best, tier)
  if (best < 0) return 2
  const per = tierValue(CREATURE_PASSIVE_VALUES.bloodbloomCost, best)
  return best >= 3 && !isApotheosis('bloodbloom', best) ? per * team.length : per
}

/** The pact's chance bonus (10 without a Lily; ×team at T3). */
export function bloodPactBonus(team: Creature[]): number {
  let best = -1
  for (const { tier } of passiveHolders(team, 'bloodbloom')) best = Math.max(best, tier)
  if (best < 0) return 10
  const per = tierValue(CREATURE_PASSIVE_VALUES.bloodbloomBonus, best)
  return best >= 3 && !isApotheosis('bloodbloom', best) ? per * team.length : per
}


// ---------- 👑 Decree + Sovereign (v3.06) — the roster's last ----------

/** How many decrees the Kirin may issue per node. */
export function decreeUsesAllowed(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'sovereign')
  return tier > 0 && isApotheosis('sovereign', tier) ? 2 : 1
}

/** 👑 The Kirin holding an armed decree, if any (v3.56 — persisted on the
 * creature so a reload cannot silently disarm it). */
export function decreeArmedCreature(state: RunState): Creature | null {
  return state.roster.find(c => c.decreeArmed && canDecree(c)) ?? null
}

export function clearDecreeArmed(state: RunState) {
  for (const creature of state.roster) creature.decreeArmed = undefined
}

export function canDecree(creature: Creature): boolean {
  return creatureOwnSkill(creature)?.id === 'decree'
    && (creature.decreeUses ?? 0) < decreeUsesAllowed(creature)
}

/**
 * The XP multiplier a decreed mission pays. v3.59 (per Javi): Decree pays
 * FULL XP on its own — Sovereign doubles it rather than being the only
 * way to get any XP at all.
 */
export function sovereignXpShare(creature: Creature): number {
  const tier = creaturePassiveTier(creature, 'sovereign')
  return tier > 0 ? tierValue(CREATURE_PASSIVE_VALUES.sovereignXpShare, tier) : 1
}

/** Is this mission decree-able? Bosses, gold and training are exempt. */
export function decreeEligible(mission: Mission): boolean {
  return mission.status === 'waiting' && mission.tier !== 'boss'
    && !mission.goldReward && !mission.training
}

/**
 * Stamps the royal order onto a dispatch: instant travel, minimal
 * fight, Sovereign's XP share and goal weight recorded, the use spent.
 */
export function applyDecree(kirin: Creature, mission: Mission) {
  mission.decreed = true
  mission.decreeXpShare = sovereignXpShare(kirin)
  const tier = creaturePassiveTier(kirin, 'sovereign')
  mission.decreedDouble = tier > 0 && isApotheosis('sovereign', tier)
  kirin.decreeUses = (kirin.decreeUses ?? 0) + 1
  kirin.skillCooldown = canDecree(kirin) ? 0 : ONCE_PER_NODE
}


// ---------- 🔍 v3.07 skill audit fixes ----------
// Eight behaviors whose tier text had NO implementation behind it,
// found by auditing every described effect against its call sites.

/** 🌹 Prospector Apotheosis: gold missions it joins pay a second time. */
export function prospectorGoldMissionMult(team: Creature[]): number {
  for (const { tier } of passiveHolders(team, 'prospector')) {
    if (isApotheosis('prospector', tier)) return 2
  }
  return 1
}

/** 🪞 Reflection Apotheosis: the copy becomes permanent at node end. */
export function reflectionOnNodeComplete(roster: Creature[]) {
  for (const { creature, tier } of passiveHolders(roster, 'reflection')) {
    if (!isApotheosis('reflection', tier)) continue
    // v4.86 (per Javi: "I thought it would save the highest stat it
    // copied from PARTNERING, not from the whole roster — then the
    // player can pick and choose what stat he wants"): only creatures
    // it actually fought beside this node count. Bench the Echopus all
    // node and it keeps nothing.
    const partners = creature.reflectionPartners ?? []
    let best = 0
    let bestElement: ElementKey | null = null
    for (const mate of roster) {
      if (mate.id === creature.id || !partners.includes(mate.id)) continue
      for (const element of ELEMENTS) {
        if (mate.stats[element] > best) { best = mate.stats[element]; bestElement = element }
      }
    }
    // v4.88 (per Javi): a SHARE, not the whole stat — two mirrors
    // (Echopus + an Understudy copy) were doubling each other forever.
    if (bestElement) creature.stats[bestElement] += Math.ceil(best * CREATURE_PASSIVE_VALUES.reflectionKeep)
  }
}

/** 🌳 Rootstock Apotheosis: rest stops feed the whole team, permanently. */
export function rootstockRestBoost(roster: Creature[]): boolean {
  const boosted = passiveHolders(roster, 'rootstock')
    .some(({ tier }) => isApotheosis('rootstock', tier))
  if (boosted) {
    for (const creature of roster) {
      for (const element of ELEMENTS) creature.stats[element]++
    }
  }
  return boosted
}

/** 🐍 Gilded Scales Apotheosis: the shop restocks once per visit. */
export function gildedRestockMult(roster: Creature[]): number {
  for (const { tier } of passiveHolders(roster, 'gilded-scales')) {
    if (isApotheosis('gilded-scales', tier)) return 2
  }
  return 1
}

/**
 * 🧘 Deep Sit Apotheosis: the banked rest survives the mission — where
 * every other holder's bank is spent by the trip it paid for.
 * (Renamed from keepsRestOnDispatch in v6.66: the bank is no longer
 * cleared AT dispatch, so the fight it was banked for can actually see
 * it; it clears when the creature gets home.)
 */
export function keepsRestAfterMission(creature: Creature): boolean {
  const tier = creaturePassiveTier(creature, 'deep-sit')
  return tier > 0 && isApotheosis('deep-sit', tier)
}

/** 😴 Power Nap Apotheosis: the nap counts even while out on a mission. */
export function restsWhileOut(creature: Creature): boolean {
  const tier = creaturePassiveTier(creature, 'power-nap')
  return tier > 0 && isApotheosis('power-nap', tier)
}

/** Fire passive: flat success-chance bonus from the team (non-boss). */
export function passiveChanceBonus(team: Creature[], run?: RunState | null, mission?: Mission): number {
  let bonus = 0
  for (const creature of team) {
    for (const passive of creaturePassives(creature)) {
      if (passive.element === 'fire') bonus += PASSIVE_VALUES.fireChance[passive.tier - 1]!
    }
  }
  // 😤 Grit (v2.80) and 🌄 First Light (v2.82) ride the same hook so the
  // assignment preview and computeMissionOutcome can never disagree.
  bonus += gritChanceBonus(team, run)
  bonus += crowdWorkBonus(team, run)
  bonus += resonanceBonus(team, run)
  bonus += ballastBonus(team, mission)
  bonus += overgrowthBonus(team, mission, run)
  if (run?.encounter) {
    // 🌄 v7.14 (code review): a dispatched mission carries its own answer
    // to "did this open the node?" - the encounter flag flips the moment
    // it ships, so re-reading it at resolution dropped the bonus the
    // preview promised (a 50% First Light dispatch resolved at 35%).
    // 🦈 v7.18 (follow-up review): only a mission that has actually shipped
    // carries a stamp worth reading - a Voracious rebirth spreads the old
    // mission, stamp included, and its ASSIGNMENT read the stale true.
    const alreadyDispatched = mission?.firstDispatch !== undefined && mission.status !== 'waiting'
      ? !mission.firstDispatch
      : (run.encounter.firstDispatchDone ?? false)
    bonus += firstLightBonus(team, run.roster, alreadyDispatched).chance
  }
  return bonus
}

/**
 * ⚔️ Fight stat (v0.91, per Javi): ONE number that shortens fight time.
 * Each point = −4% off the team's fight timer, SUMMED across the team,
 * capped at −70%. Everything that used to touch fight time feeds it:
 * innate `creature.fight` (baseline 0), Storm Tempo passives, the
 * 🥁 Pacesetter trained skill (+6), and 💨/⚓ stacks (+1 each).
 * (Boss fight multipliers and Focus Bell charges stay mission-side.)
 */
export const FIGHT_POINT_VALUE = 0.04
export const FIGHT_MULT_FLOOR = 0.3

export function creatureFightStat(creature: Creature, run?: RunState | null): number {
  let points = creature.fight ?? 0
  for (const passive of creaturePassives(creature)) {
    if (passive.element === 'lightning') points += PASSIVE_VALUES.lightningFight[passive.tier - 1]!
  }
  if (hasTrainedSkill(creature, 'pacesetter')) points += 6
  points += (creature.momentumStacks ?? 0) + (creature.impulseStacks ?? 0)
  points += starweaveFightBonus(creature, run)
  return points
}

export function teamFightPoints(team: Creature[], run?: RunState | null): number {
  return team.reduce((sum, creature) => sum + creatureFightStat(creature, run) + starweaveDispatchBonus(creature, team, run).fight, 0)
}

/** The multiplier a team's summed ⚔️ Fight applies to fight timers. */
export function teamFightTimeMult(team: Creature[], run?: RunState | null): number {
  // 🌊 Deluge's rider is a per-creature Fight bonus on this mission.
  const points = teamFightPoints(team, run) + tideDelugeRider(team).fight * team.length
  return Math.max(FIGHT_MULT_FLOOR, 1 - FIGHT_POINT_VALUE * points)
}

/** Earth passive: total failure-damage reduction from the team. */
export function passiveFailureReduction(team: Creature[]): number {
  let reduction = 0
  for (const creature of team) {
    for (const passive of creaturePassives(creature)) {
      if (passive.element === 'earth') reduction += PASSIVE_VALUES.earthReduction[passive.tier - 1]!
    }
  }
  // 🛡️ Thornmail (v2.80) stacks with Stoneguard on the same hook.
  return reduction + thornmailReduction(team)
}

/** 🪨 v4.16 (per Javi: "buff Stoneguard to include expired missions"):
 * Stoneguard carriers WAITING AT CAMP absorb part of any expiry bite.
 * Earth only — Thornmail's text promises failures and keeps to them —
 * and only from creatures neither dispatched nor benched: the guard has
 * to actually be home to guard it. 🪨 v7.77: nor holding a far site (Hold
 * Position keeps it available but away - ForestBoard's camp is the same rule). */
export function stoneguardExpiryReduction(roster: Creature[]): number {
  let reduction = 0
  for (const creature of roster) {
    if (!creature.available || creature.benched || creature.stationedPoi !== undefined) continue
    for (const passive of creaturePassives(creature)) {
      if (passive.element === 'earth') reduction += PASSIVE_VALUES.earthReduction[passive.tier - 1]!
    }
  }
  return reduction
}

/** §14 expiry bite, all softeners in one place: reduced share of the
 * mission's damage, spared whole by Cremation, chipped by camped
 * Stoneguards (v4.16) and by Safety Margin (v4.25, per Javi: "buff
 * safety margin to also include expired missions"). Both the live
 * expiry and the discard button — and the discard PREVIEW — read this,
 * so the number shown is the number that lands. */
export function expiryDamageFor(damage: number, roster: Creature[], run?: RunState | null): number {
  if (damage <= 0) return 0
  if (cremationSparesDamage(roster)) return 0
  const base = Math.max(1, Math.round(damage * TUNING.encounters.expiredDamageMult))
  const margin = run && hasRelic(run, 'relic-safety-margin') ? 1 : 0
  return Math.max(0, base - stoneguardExpiryReduction(roster) - margin)
}

/** ⏫ v4.72 (per Javi: "missions should gradually start dealing more
 * and more damage as time goes on, so infinite farming is not
 * possible"): the damage multiplier for a mission SPAWNING this many
 * seconds after the enrage armed. Linear and uncapped — baked in at
 * spawn (never applied to a live card) so the number shown is always
 * the number dealt. */
export function escalationMult(enrageSeconds: number): number {
  return 1 + TUNING.encounters.escalationPerMinute * (Math.max(0, enrageSeconds) / 60)
}

/** How long this node's enrage has been running — 0 until the trigger
 * spawn count is reached (v4.75, per Javi: the FIFTH spawn arms it,
 * not a fixed grace; the tick stamps `enrageAt` when it happens). */
export function enrageSeconds(enc: { secondsElapsed?: number; enrageAt?: number } | null | undefined): number {
  if (!enc || typeof enc.enrageAt !== 'number') return 0
  return Math.max(0, (enc.secondsElapsed ?? 0) - enc.enrageAt)
}

/** Grass passive: healing granted when this team's mission succeeds.
 * Returns [healTotal, xpBonusCreatureIds (tier-3 self-feeders)]. */
export function passiveSuccessHealing(team: Creature[]): { heal: number; bonusXpIds: string[] } {
  let heal = 0
  const bonusXpIds: string[] = []
  for (const creature of team) {
    for (const passive of creaturePassives(creature)) {
      if (passive.element === 'grass') {
        heal += PASSIVE_VALUES.grassHeal[passive.tier - 1]!
        if (passive.tier >= 3 && !bonusXpIds.includes(creature.id)) bonusXpIds.push(creature.id)
      }
    }
  }
  return { heal, bonusXpIds }
}

/** +1 to every element tied for the extreme (min ignores zeros), in place. */
function boostExtreme(stats: Stats, pick: 'max' | 'min') {
  const values = ELEMENTS.map(e => stats[e]).filter(v => pick === 'max' || v > 0)
  if (values.length === 0) return
  const target = pick === 'max' ? Math.max(...values) : Math.min(...values)
  for (const element of ELEMENTS) {
    if (stats[element] === target) stats[element]++
  }
}

/**
 * A creature's live stats: allocated stats plus dynamic modifiers, in
 * order — relic stat effects (charms; Duo Core / Specialist Badge, whose
 * conditions read the allocated stats), then Elemental Spark / Balanced
 * Training recomputed from current totals, then the injury penalty
 * floored at 0.
 */
/** Activated trained skills that ride a mission (📖). Pain Into Power
 * became a true passive in v1.89 (per Javi) — no arming. */
// Empty since v2.27 (Tough Learner went passive) — the arm-chip UI and
// tutorial tip key off this list and disappear with it.
export const ARMABLE_SKILL_IDS: string[] = []

/** Consumes 📖/🔥 charges once their mission resolves: each consumed
 * charge starts its 30s re-arm clock (v1.12, per Javi — the same
 * cadence as surges). Returns the surviving armed list. */
export function consumeArmedSkills(creature: Creature): string[] {
  const armed = creature.armedSkills ?? []
  for (const id of ARMABLE_SKILL_IDS) {
    if (armed.includes(id)) {
      creature.armedSkillCooldowns = {
        ...(creature.armedSkillCooldowns ?? {}),
        [id]: TUNING.skills.trainedCooldownSeconds,
      }
    }
  }
  return armed.filter(id => !ARMABLE_SKILL_IDS.includes(id))
}

/** 🧘 The Equilibrium spread: the allocation redistributed evenly, the
 * remainder landing on the strongest elements first (deterministic). */
export function equalizedSpread(source: Stats): Stats {
  const total = ELEMENTS.reduce((sum, e) => sum + source[e], 0)
  const base = Math.floor(total / ELEMENTS.length)
  let remainder = total - base * ELEMENTS.length
  const order = [...ELEMENTS].sort((a, b) => source[b] - source[a])
  const out = emptyStats()
  for (const element of ELEMENTS) out[element] = base
  for (const element of order) {
    if (remainder <= 0) break
    out[element]++
    remainder--
  }
  return out
}

/** The stats a creature is presently BUILT as: its true allocation, or
 * the even spread while Equilibrium mode is on (v1.12 — the toggle no
 * longer rewrites the allocation, so it can always flip back). */
export function allocatedStats(creature: Creature): Stats {
  return creature.equalized ? equalizedSpread(creature.stats) : { ...creature.stats }
}

export function effectiveStats(creature: Creature, run?: RunState | null): Stats {
  const stats = allocatedStats(creature)
  // 💪 Colossus (reworked v0.92, per Javi — the old post-everything
  // doubling was hard to read): its highest ALLOCATED element gains a
  // flat bonus equal to those points. A stable green +N that only moves
  // when stat points are spent — surges, charms and stacks never
  // change it.
  if (hasTrainedSkill(creature, 'colossus')) {
    const top = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
    stats[top] += creature.stats[top]
  }
  // Armed surge skill: applies until the creature returns home.
  // 🔋 Overcharge Cell (v0.93, per Javi): surges hit twice as hard.
  if (creature.surgePrimed) {
    const skill = creatureSkill(creature, run)
    // A LENT surge (Overcharge on a surge-less creature) is not doubled.
    const overcharge = surgeCellMult(skill, run)
    for (const element of skill.elements) stats[element] += skill.bonus * overcharge
  }
  // 🧘 Deep Sit (v2.82): 🪨 banked while resting at home, spent on its
  // next fight. Sits in effectiveStats so it renders as a bonus chip and
  // reaches the chance calc without a second code path.
  stats.earth += deepSitBonus(creature)
  // 🔥 Overheat (v2.86): the armed carrier's own mission burns hotter.
  stats.fire += overheatBonus(creature)
  // 🩸 Last Stand / 😴 Power Nap (v2.87): both are per-creature, so they
  // ride effectiveStats and show up as green bonus chips.
  const desperate = lastStandBonus(creature, run)
  const napped = powerNapBonus(creature)
  // 🏺 Reliquary (v2.94): relic count becomes a stat.
  const hoard = reliquaryStats(creature, run)
  for (const element of ELEMENTS) stats[element] += hoard[element]
  // 🎭 Routine (v4.68, per Javi): the copied statline rides ON TOP of
  // his own until node end (it replaced it before).
  // 🌀 v6.91 Blackhole: the swallowed mission's requirements, worn until his next deployment resolves.
  if (creature.blackholeStats) for (const element of ELEMENTS) stats[element] += creature.blackholeStats[element]
  if (creature.routineStats) {
    for (const element of ELEMENTS) stats[element] += creature.routineStats[element]
  }
  // 🔷 Refract (v2.92): a copied statline REPLACES its own while worn.
  if (creature.refractStats) {
    for (const element of ELEMENTS) stats[element] = creature.refractStats[element]
  }
  // 🤭 Infectious Giggle (v2.92): the borrowed element rides along.
  const giggle = giggleBonusFor(creature)
  if (giggle) stats[giggle.element] += giggle.amount
  // 🎭 Mimic (v3.05): the copied surge rides until its mission resolves.
  if (creature.mimicElements && (creature.mimicBonus ?? 0) > 0) {
    for (const element of creature.mimicElements) stats[element] += creature.mimicBonus!
  }
  // 🌬️ Second Wind / 🔋 Static Charge (v2.91) ride the same all-element chip.
  const wind = secondWindBonus(creature)
  const charged = staticChargeBonus(creature)
  const bedrock = bedrockBonus(creature)
  // 🐻 Hibernal banks straight into stats since v4.66 — no rider here.
  const dragonGold = dragonsHoardBonus(creature, run)
  // 🕊️ v6.90 The Long Silence: the sage's quiet, worn by everyone.
  const silence = silenceBonus(run)
  if (desperate || napped || wind || charged || bedrock || dragonGold || silence) {
    for (const element of ELEMENTS) stats[element] += desperate + napped + wind + charged + bedrock + dragonGold + silence
  }
  // A worn Refract snapshot already carries the target's run-wide layer
  // (charms, duo core, balanced/spark) — adding the wearer's would count
  // it twice. Routine keeps the layer: its copy is a share of BASE.
  if (run && !creature.refractStats) {
    const prism = refractionExtras(creature, run)
    // 🔮 v7.69 (per Javi): a Master Collector's charms count double.
    const charmWorth = hasStarweave(run, 'nova-discovery') ? 2 : 1
    for (const element of ownedCharmElements(run)) stats[element] += charmWorth + prism.charmExtra
    // 🔮 Its surges gain +N per owned charm (tier 3+), on the surge elements.
    if (prism.surgePerCharm > 0 && creature.surgePrimed) {
      const owned = ownedCharmElements(run).length
      const skill = creatureSkill(creature, run)
      for (const element of skill.elements) stats[element] += prism.surgePerCharm * owned
    }
    const nonZero = ELEMENTS.filter(e => creature.stats[e] > 0)
    if (hasRelic(run, 'relic-duo-core') && nonZero.length === 2) {
      for (const element of nonZero) stats[element]++
    }
    // Both can be owned as relics: Balanced Training raises the floor
    // first, then Elemental Spark boosts the resulting peak. (bonusId
    // checks cover saves from before these became relics.)
    if (run.bonusId === 'balanced-training' || hasRelic(run, 'relic-balanced-training')) {
      boostExtreme(stats, 'min')
    }
    if (run.bonusId === 'elemental-spark' || hasRelic(run, 'relic-elemental-spark')) {
      boostExtreme(stats, 'max')
    }
  }
  // Starweave points are an additive run layer, never allocated stats for
  // Colossus or a doubled Surge to multiply. Injuries still soften them.
  const starweave = starweaveElementBonus(creature, run)
  for (const element of ELEMENTS) stats[element] += starweave[element]
  if (creature.injured && injuriesActive(run)) {
    // 🤕 v5.41 (per Javi): −10% per stack, floored — scales with the
    // statline instead of vanishing under it.
    const mult = Math.max(0, 1 - TUNING.injury.statPenaltyPct * injuryStacks(creature))
    for (const element of ELEMENTS) {
      stats[element] = Math.floor(stats[element] * mult)
    }
  }
  // 💨 Momentum drains its lowest >0 element per stack (never below 0).
  for (let i = 0; i < (creature.momentumStacks ?? 0); i++) {
    const open = ELEMENTS.filter(e => stats[e] > 0)
    if (open.length === 0) break
    stats[open.reduce((a, b) => (stats[b] < stats[a] ? b : a))]--
  }
  // ⚓ Impulse (v0.86; NERFED v2.70, per Javi — "it's too strong"): each
  // stack was +1 to ALL FIVE elements, zeros included, with no cap and
  // no reset between nodes — five points a success, which outran every
  // requirement curve in the game. A stack is now +1 to its HIGHEST and
  // +1 to its LOWEST ABOVE ZERO: two points, and the creature has to
  // already own an element to grow it, so Impulse sharpens a build
  // instead of erasing the idea of coverage.
  //
  // One element each, never a tie-broadcast: `boostExtreme` would bump
  // every tied element, which on a 1/1/1/1/1 spread is +2 to ALL FIVE —
  // stronger than what we are nerfing. Ties resolve to the first in
  // ELEMENTS order, matching Momentum's drain loop above.
  for (let i = 0; i < (creature.impulseStacks ?? 0); i++) {
    const high = ELEMENTS.reduce((a, b) => (stats[b] > stats[a] ? b : a))
    stats[high]++
    const open = ELEMENTS.filter(e => stats[e] > 0)
    if (open.length === 0) continue
    stats[open.reduce((a, b) => (stats[b] < stats[a] ? b : a))]++
  }
  return stats
}

/** The live modifier share of a creature's stats — everything relics
 * and an armed surge add on top of the allocated base (green +N), and
 * everything injuries/momentum take away (red −N; v1.12, per Javi —
 * the old Math.max(0, …) clamp made injury penalties invisible). */
/** The line a creature is presently WEARING, for display anchoring:
 * copy-layer states replace the build outright (Refract wins over
 * Routine, matching effectiveStats' order), so chips anchor on the
 * copy — v4.35, per Javi: the Mime's copied line "only shows when
 * dispatched" because home surfaces anchored on the raw build. */
export function presentedStats(creature: Creature): Stats {
  if (creature.refractStats) return { ...creature.refractStats }
  // 🎭 Routine is ADDITIVE since v4.68 — the copy shows as green bonus
  // chips over the mime's own line, not as a replacement base.
  return allocatedStats(creature)
}

export function bonusStats(creature: Creature, run?: RunState | null): Stats {
  // 🧘 v4.30 (Javi: "my original stats are contributing on top of the
  // equilibrium stats"): diff against the PRESENTED line. While
  // Equilibrium is on that's the even spread; while a copy is worn
  // (v4.35) it's the copy — diffing against the raw build painted
  // phantom +N/−N chips that summed the two lines.
  //
  // 🤕 v5.31 built the badge from a cured twin because the old FLAT
  // penalty's 0-floor hid part of the toll (a Lv 1 Mime with two stacks
  // showed −1). v5.41's percentage penalty (−10%/stack of what you
  // actually have, floored) never clamps anything away — the plain diff
  // against the presented line IS the whole truth again.
  const base = presentedStats(creature)
  const actual = effectiveStats(creature, run)
  const out = emptyStats()
  for (const element of ELEMENTS) {
    out[element] = actual[element] - base[element]
  }
  return out
}

/** Combined effective stats of a team of creatures. */
export function teamStats(creatures: Creature[], run?: RunState | null): Stats {
  const total = emptyStats()
  for (const creature of creatures) {
    const stats = effectiveStats(creature, run)
    for (const element of ELEMENTS) total[element] += stats[element]
  }
  return total
}

// ---- Boss skills (§18): pacing constants for the real-time pressure
// skills. Their clocks run ONLY while the boss mission detail is open —
// the rest of the encounter stays paused while the player plans.
export const ECLIPSE_ROTATE_SECONDS = 6
// v0.50: growth ticks on the FIELD clock too (not just while the detail
// is open) and comes faster — the crunch is real.
export const UNSTABLE_GROW_SECONDS = 3
export const UNSTABLE_ROTATE_SECONDS = 10
export const CORRUPTED_DAMAGE_PER_POINT = 5
export const FRACTURE_INHERITANCE = 0.9
/** Fracture splits EXPIRE (v0.51) — the player must divide their forces
 * instead of full-squadding each piece in sequence. Expired splits deal
 * normal expiry damage and reform. */
export const FRACTURE_SPLIT_EXPIRE_SECONDS = 45

/**
 * Team stats as THIS mission sees them, with boss skills applied:
 * Cursed Slot zeroes the FIRST creature's strongest relevant element
 * (creature order = pick order), and Element Eclipse zeroes the whole
 * team's contribution to the eclipsed element. Missions without a boss
 * skill behave exactly like teamStats.
 */
export function missionTeamStats(mission: Mission, creatures: Creature[], run?: RunState | null): Stats {
  return missionTeamStatsDetailed(mission, creatures, run).total
}

/** The same assembly, also reporting Equanimity's floating fill so the
 * assignment card can paint the points on the Sage's row (v5.11 — the
 * player must SEE where the pool went and how it moves the %). */
export function missionTeamStatsDetailed(
  mission: Mission, creatures: Creature[], run?: RunState | null,
): { total: Stats; equanimity: Stats } {
  const total = emptyStats()
  creatures.forEach((creature, index) => {
    const stats = effectiveStats(creature, run)
    // 🧤 v6.89 Aid's Anticipation: his statline is read in the shape the
    // mission wants (and Mr. Khool's at the Apotheosis, when they ride together).
    anticipationRelabel(creature, creatures, mission, stats)
    // 🐣 Underdog / 🪞 Reflection (v2.87) need the TEAM, which
    // effectiveStats cannot see — they are folded in here instead.
    const context = teamContextBonus(creature, creatures)
    for (const element of ELEMENTS) stats[element] += context[element]
    if (creatures.length === 2 && hasStarweave(run, 'attunement-left')) {
      const natural = ELEMENTS.filter(e => getCreatureDef(creature.defId).stats[e] > 0)
      if (natural.length === 1) stats[natural[0]!]++
    }
    const haunt = wispVirtualStats(creature, mission)
    for (const element of ELEMENTS) stats[element] += haunt[element]
    // 🕯️ v5.27 (per Javi): Hexa curses the first N slots by ascension.
    if (mission.bossSkill === 'cursed' && index < bossAfflictionCount(run?.act, run?.roster.length)) {
      const relevant = ELEMENTS.filter(e => mission.required[e] > 0)
      if (relevant.length > 0) {
        const strongest = relevant.reduce((a, b) => (stats[b] > stats[a] ? b : a))
        stats[strongest] = 0
      }
    }
    for (const element of ELEMENTS) total[element] += stats[element]
  })
  // 🎯 v6.92 Robodude's Fire Support: the gunner's full statline counts for
  // the mission he is shelling, from where he stands, without a slot.
  if (mission.fireSupportId && run) {
    const gunner = run.roster.find(c => c.id === mission.fireSupportId && c.defId === 'robodude')
    if (gunner && !creatures.some(c => c.id === gunner.id)) {
      const shells = effectiveStats(gunner, run)
      for (const element of ELEMENTS) total[element] += shells[element]
    }
  }
  // 🦔 Thorn Toll (REDESIGNED v5.14): every Quillhog on the ROSTER
  // softens every mission on the field — no priming, no aboard
  // requirement. Without run context (tests, bare previews) the team
  // itself carries the aura.
  for (const { creature } of passiveHolders(run?.roster ?? creatures, 'thorn-toll')) {
    const toll = thornTollVirtualStats(creature, mission)
    for (const element of ELEMENTS) total[element] += toll[element]
  }
  // 🌈 Refraction Cascade (v2.97): the serpent's surge washes over the
  // whole team — mult× to the others, the balance to itself.
  const tide = tideDelugeTeamBonus(creatures, run)
  if (tide) {
    for (const element of tide.elements) {
      total[element] += tide.amount * creatures.length - tide.own
    }
  }
  const cascade = cascadeTeamBonus(creatures, run)
  if (cascade) {
    // The serpent's own 1x (`own`) is already in via effectiveStats;
    // top the team total up to amount x teamSize.
    for (const element of cascade.elements) {
      total[element] += cascade.amount * creatures.length - cascade.own
    }
  }
  if (mission.bossSkill === 'eclipse' && mission.eclipsedElement) {
    total[mission.eclipsedElement] = 0
  }
  // ☯️ Equanimity (v5.11): the floating pool fills whatever is STILL
  // missing after every real contribution — last word before the
  // resolver, and equanimityFill itself skips an eclipsed element.
  const zen = equanimityFill(creatures, mission, total)
  for (const element of ELEMENTS) total[element] += zen[element]
  return { total, equanimity: zen }
}

/**
 * Unstable Requirements: advances the growth/rotation clocks by dt
 * seconds. Runs on the FIELD clock while the mission waits AND in real
 * time while the detail is open (the game clock pauses there) — shared
 * by the live game and both simulations.
 */
export function tickUnstable(mission: Mission, dt: number) {
  if (mission.bossSkill !== 'unstable' || mission.status !== 'waiting' || !mission.unstableElement) return
  mission.unstableGrowIn = (mission.unstableGrowIn ?? UNSTABLE_GROW_SECONDS) - dt
  if (mission.unstableGrowIn <= 0) {
    mission.required[mission.unstableElement]++
    mission.unstableGrowIn += UNSTABLE_GROW_SECONDS
  }
  mission.unstableRotateIn = (mission.unstableRotateIn ?? UNSTABLE_ROTATE_SECONDS) - dt
  if (mission.unstableRotateIn <= 0) {
    const elements = ELEMENTS.filter(e => mission.required[e] > 0)
    const index = elements.indexOf(mission.unstableElement)
    mission.unstableElement = elements[(index + 1) % elements.length] ?? mission.unstableElement
    mission.unstableRotateIn += UNSTABLE_ROTATE_SECONDS
  }
}

/**
 * Boss Feint, applied ONCE at dispatch: the telegraphed requirement
 * scatters point by point onto the other required elements.
 */
export function applyFeint(mission: Mission) {
  const element = mission.feintElement
  if (mission.bossSkill !== 'feint' || !element || mission.required[element] <= 0) return
  const others = ELEMENTS.filter(e => e !== element && mission.required[e] > 0)
  if (others.length === 0) return
  const points = mission.required[element]
  mission.required[element] = 0
  const gains: Partial<Record<ElementKey, number>> = {}
  for (let i = 0; i < points; i++) {
    const target = others[Math.floor(Math.random() * others.length)]!
    mission.required[target]++
    gains[target] = (gains[target] ?? 0) + 1
  }
  // Recorded so the player can SEE how the feint landed after committing.
  mission.feintScatter = { from: element, points, gains }
  mission.feintElement = undefined
}

export interface BossSplitSpec {
  required: Stats
  damage: number
  xp: number
  slots: number
  depth: number
  /** Split label ("🔥 Core", "Yin"…). */
  label: string
}

/**
 * Fracture: a beaten multi-element fragment splits into two children
 * that partition its elements and inherit 90% of each requirement;
 * damage and XP scale down with the split. Single-element fragments are
 * terminal — beat them all and the node falls. Returns statline specs;
 * the caller (game or sim) turns them into missions.
 */
export function fractureChildren(mission: Mission): BossSplitSpec[] {
  if (mission.bossSkill !== 'fracture') return []
  const elements = ELEMENTS.filter(e => mission.required[e] > 0)
  if (elements.length <= 1) return []
  const half = Math.ceil(elements.length / 2)
  const groups = [elements.slice(0, half), elements.slice(half)]
  return groups.map((group) => {
    const required = emptyStats()
    for (const element of group) {
      required[element] = Math.max(1, Math.round(mission.required[element] * FRACTURE_INHERITANCE))
    }
    return {
      required,
      damage: Math.max(1, Math.round((mission.damage * FRACTURE_INHERITANCE) / groups.length)),
      xp: Math.max(1, Math.round((mission.xp * FRACTURE_INHERITANCE) / groups.length)),
      // Tight crews only (v0.51): half the parent's slots, so a full
      // squad can't steamroll each piece one by one.
      slots: Math.max(1, Math.floor(mission.slots / 2)),
      depth: (mission.fractureDepth ?? 0) + 1,
      label: group.length === 1 ? 'Core' : 'Fragment',
    }
  })
}

/**
 * Yin & Yang: the beaten whole sunders into two timed halves, each with
 * two of the four required elements at FULL value. Splits are terminal.
 */
export function yinYangSplits(mission: Mission): BossSplitSpec[] {
  if (mission.bossSkill !== 'yinyang' || (mission.fractureDepth ?? 0) > 0) return []
  const elements = ELEMENTS.filter(e => mission.required[e] > 0)
  if (elements.length < 2) return []
  const half = Math.ceil(elements.length / 2)
  const groups = [elements.slice(0, half), elements.slice(half)]
  return groups.map((group, index) => {
    const required = emptyStats()
    for (const element of group) required[element] = mission.required[element]
    return {
      required,
      damage: Math.max(1, Math.round(mission.damage / 2)),
      xp: Math.max(1, Math.round(mission.xp / 2)),
      slots: Math.max(1, Math.floor(mission.slots / 2)),
      depth: 1,
      label: index === 0 ? 'Yin' : 'Yang',
    }
  })
}

/**
 * ⚔️ The gauntlet requirement ramp, two-stage since v4.38 (per Javi:
 * "ramp up even more starting after act 4 — ideally the perfect set up
 * gets to act 12 ish"; a 5-hour act-27 run prompted the second stage).
 * extraActs = acts past the scaling table (act − 3): the first
 * `gauntletLateRampFromExtra` of them compound at the classic rate,
 * the rest at the steeper late rate. Lives here (not missiongen) so
 * bloodPriceReduction shares it without an import cycle.
 */
export function gauntletRampMult(extraActs: number): number {
  const { gauntletRampPerAct, gauntletLateRampPerAct, gauntletLateRampFromExtra, gauntletWallRampPerAct, gauntletWallRampFromExtra } = TUNING.acts
  const extra = Math.max(0, extraActs)
  const early = Math.min(extra, gauntletLateRampFromExtra)
  // ⚔️ v5.52 three-stage: classic → steep → THE WALL (act 13+).
  const late = Math.min(extra, gauntletWallRampFromExtra) - early
  const wall = Math.max(0, extra - gauntletWallRampFromExtra)
  return gauntletRampPerAct ** early * gauntletLateRampPerAct ** late * gauntletWallRampPerAct ** wall
}

/** All splitting bosses funnel through here (game + sims). */
export function bossSplits(mission: Mission): BossSplitSpec[] {
  if (mission.bossSkill === 'fracture') return fractureChildren(mission)
  if (mission.bossSkill === 'yinyang') return yinYangSplits(mission)
  return []
}

/** True for splitting-boss pieces that expire/reform and gate completion. */
export function isBossSplit(mission: Mission): boolean {
  return mission.tier === 'boss'
    && (mission.bossSkill === 'fracture' || mission.bossSkill === 'yinyang')
    && (mission.fractureDepth ?? 0) > 0
}

/**
 * ⚔️ OVERKILL: points committed OVER a mission's requirement, counted
 * only on the elements it actually requires.
 *
 * Shared on purpose (v2.71): Yin & Yang's transfer and the Windfall
 * wedge must never measure overkill differently, or the boss fight and
 * the spinner would disagree about the same dispatch.
 */
export function overkillPoints(required: Stats, committed: Stats): number {
  let over = 0
  for (const element of ELEMENTS) {
    if (required[element] > 0) {
      over += Math.max(0, committed[element] - required[element])
    }
  }
  return over
}

/**
 * 🪙 WINDFALL (v2.71, per Javi): the spinner's THIRD wedge — a win that
 * also pays something extra, carved out of the WIN share, never the loss
 * share. At 100% chance with a 10% Windfall relic the wheel reads
 * 90% green / 10% gold / 0% red.
 *
 * Deliberately RELIC-GATED, per gamedesign.md §"Important Rule": *"Overkill
 * does not provide a default bonus. Overkill bonuses may be added later
 * through relics, traits, or skills."* No relic → 0 → today's wheel,
 * unchanged. Nothing existing was rebalanced to make room for this.
 *
 * Percentages from multiple relics SUM into one gold slice, and that one
 * slice pays every payload the player owns. Overkill is one optional
 * SOURCE of extra wedge, not the definition of it.
 */
export interface WindfallRelic {
  id: string
  /** Flat share of the win converted to a Windfall. */
  pct: number
  /** Extra wedge per point of Overkill committed. */
  perOverkill?: number
}

/** Prospector's Cut payout on a golden win. */
export const WINDFALL_GOLD = 10

/** 💨⚓ v5.02 (per Javi): Momentum/Impulse stack to 5 — training BOTH
 * lifts the cap, their opposing costs making the infinite ride an
 * earned two-skill build. Enforced at the success increment in
 * useEncounter; stacks already above the cap stop climbing, never shrink.
 * v7.63 (per Javi): the lift is EARNED - both engines must reach 5 first
 * (5 Momentum + 1 Impulse: Momentum waits at 5 until Impulse is at 5, then
 * both move on to 6). */
export const MOMENTUM_STACK_CAP = 5
export function engineCapLifted(creature: Creature): boolean {
  return hasTrainedSkill(creature, 'momentum') && hasTrainedSkill(creature, 'impulse')
    && (creature.momentumStacks ?? 0) >= MOMENTUM_STACK_CAP && (creature.impulseStacks ?? 0) >= MOMENTUM_STACK_CAP
}

export const WINDFALL_RELICS: WindfallRelic[] = [
  { id: 'relic-prospectors-cut', pct: 10 },
]

export function windfallChance(
  state: RunState | null | undefined,
  mission: Mission,
  committed: Stats,
  chance: number,
): number {
  // Training missions never roll, so they can never pay a Windfall.
  if (!state || mission.training || chance <= 0) return 0
  let pct = starweaveWindfallChance(state)
  let perOverkill = 0
  for (const relic of WINDFALL_RELICS) {
    if (!hasRelic(state, relic.id)) continue
    pct += relic.pct
    perOverkill += relic.perOverkill ?? 0
  }
  if (pct <= 0 && perOverkill <= 0) return 0
  if (perOverkill > 0) pct += overkillPoints(mission.required, committed) * perOverkill
  // The wedge is a SUBDIVISION of the win — it can never exceed it, so
  // win rate is untouched and difficulty needs no re-tuning.
  return Math.max(0, Math.min(Math.round(chance), Math.round(pct)))
}

/**
 * Yin & Yang's link: any points committed OVER a half's requirement (on
 * its required elements) feed its twin, scattering onto the twin's
 * required elements. Call at dispatch; mutates the sibling in place.
 */
/** ⚖️ The deterministic scatter (v4.31, Javi's report: "when they split
 * and it's time to resolve, my win percentage changes"): the transfer
 * used to scatter RANDOMLY, so no planning badge could ever predict the
 * twin's final bar. Round-robin over the twin's required elements,
 * biggest first — shared by the live transfer AND the planning
 * prediction below, so they cannot disagree. */
export function distributeOverkill(required: Stats, over: number): void {
  const targets = [...ELEMENTS]
    .filter(e => required[e] > 0)
    .sort((a, b) => required[b] - required[a] || ELEMENTS.indexOf(a) - ELEMENTS.indexOf(b))
  if (targets.length === 0) return
  for (let i = 0; i < over; i++) {
    required[targets[i % targets.length]!]++
  }
}

export function applyOverkillTransfer(mission: Mission, committed: Stats, missions: Mission[]): number {
  if (mission.bossSkill !== 'yinyang' || (mission.fractureDepth ?? 0) === 0) return 0
  const sibling = missions.find(m =>
    m.id !== mission.id && m.bossSkill === 'yinyang' && (m.fractureDepth ?? 0) > 0
    && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
  )
  if (!sibling) return 0
  const over = overkillPoints(mission.required, committed)
  if (over === 0) return 0
  distributeOverkill(sibling.required, over)
  return over
}

/**
 * ⚖️ Planning-side twin prediction (v4.31): simulate the Strike-as-One
 * transfers in DISPATCH ORDER over the pieces' current teams, returning
 * each piece's final requirement line. With the deterministic scatter
 * above, the badge you lock is exactly the bar the wheel spins.
 */
export function predictSplitRequirements(
  pieces: Array<{ mission: Mission; team: Creature[] }>,
  run?: RunState | null,
): Record<string, Stats> {
  const predicted: Record<string, Stats> = {}
  for (const { mission } of pieces) predicted[mission.id] = { ...mission.required }
  for (const { mission, team } of pieces) {
    if (mission.bossSkill !== 'yinyang' || team.length === 0) continue
    const sibling = pieces.find(p => p.mission.id !== mission.id && p.mission.bossSkill === 'yinyang')
    if (!sibling) continue
    const committed = missionTeamStats(mission, team, run)
    const over = overkillPoints(predicted[mission.id]!, committed)
    distributeOverkill(predicted[sibling.mission.id]!, over)
  }
  return predicted
}

/** Curse of Excess growth per act (v0.97, per Javi — the flat +5 was
 * brutal on an act-1 Faust and a shrug on act 3). v4.77 (per Javi:
 * "it only added 5 stats in act 9 gauntlet"): past act 3 the bump
 * rides the gauntlet requirement ramp, so the curse keeps the same
 * RELATIVE weight against the walls it curses at every depth —
 * act 4 ≈ +8 per element, act 9 ≈ +131. */
export const CURSE_EXCESS_BY_ACT = [1, 3, 5] as const

export function curseExcessBump(act: number): number {
  const base = CURSE_EXCESS_BY_ACT[Math.max(0, Math.min(CURSE_EXCESS_BY_ACT.length, act) - 1)]!
  return Math.round(base * gauntletRampMult(act - TUNING.acts.scaling.length))
}

/** Faust's three curses; choosing mutates the mission (choice 3 grows
 * every element immediately, scaled by act). Shared with the sims. */
export function applyCurseChoice(mission: Mission, choice: 1 | 2 | 3, act = 1) {
  if (mission.bossSkill !== 'curse' || mission.curseChoice) return
  mission.curseChoice = choice
  if (choice === 3) {
    for (const element of ELEMENTS) mission.required[element] += curseExcessBump(act)
  }
}

/** Curse effects on the final chance (boss coverage-as-chance). */
export function curseAdjustedChance(mission: Mission, chance: number, teamSize: number): number {
  if (mission.bossSkill !== 'curse' || !mission.curseChoice) return chance
  if (mission.curseChoice === 1) chance -= 5 * Math.max(0, teamSize - 1)
  if (mission.curseChoice === 2) chance = Math.min(chance, 75)
  return Math.min(100, Math.max(0, chance))
}

/** Basalt's crystals: each one smashed drains this much from the
 * Golem's strongest requirement — even mid-fight. */
export const CRYSTAL_DRAIN = 2

/** Where the NEXT crystal's drain will land (v4.36, per Javi: "show how
 * much is weakened") — shared with the applier so the resolution card's
 * line is exactly what happens. */
export function crystalDrainTarget(missions: Mission[]): { element: ElementKey; amount: number } | null {
  const golem = missions.find(m =>
    m.tier === 'boss' && m.bossSkill === 'golem'
    && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
  )
  if (!golem) return null
  const elements = ELEMENTS.filter(e => golem.required[e] > 0)
  if (elements.length === 0) return null
  const strongest = elements.reduce((a, b) => (golem.required[b] > golem.required[a] ? b : a))
  return { element: strongest, amount: Math.min(CRYSTAL_DRAIN, golem.required[strongest]) }
}

export function applyCrystalReduction(missions: Mission[]): boolean {
  const drain = crystalDrainTarget(missions)
  if (!drain) return false
  const golem = missions.find(m =>
    m.tier === 'boss' && m.bossSkill === 'golem'
    && ['waiting', 'traveling', 'assigned', 'resolving'].includes(m.status),
  )!
  golem.required[drain.element] = Math.max(0, golem.required[drain.element] - CRYSTAL_DRAIN)
  return true
}

/** Blood price: Sanguis trades 4 HP for a cut to one requirement. */
export const BLOOD_PRICE_HP = 4
/** The act-1 cut, and the floor for every act after it. */
export const BLOOD_PRICE_REDUCTION = 3
/** Exponent on the boss's own requirement scaling — under 1 so the cut
 * trails him further with every act. 0.9 → 0.5 in v3.88 (per Javi: "the
 * scale factor is too high, nerf by a good amount"). */
export const BLOOD_PRICE_SCALE_EXP = 0.5
/**
 * 🩸 v3.69 (per Javi): the cut SCALES with depth, so a flat −3 is not
 * loose change by the time requirements roll at 40. v3.88 rebalance: at
 * exponent 0.9 the cut TRACKED the boss — 4 HP bought −17 in act 5, and
 * three clicks erased an element. At 0.5 it's the square root of his
 * curve: still always worth paying, never close to finishing the job.
 *
 *     act 1  ×1.0 → −3 (was −3)     act 4  ×5.4 → −7 (was −13)
 *     act 2  ×2.3 → −5 (was −6)     act 5  ×7.4 → −8 (was −17)
 *     act 3  ×3.9 → −6 (was −10)    act 6 ×10.1 → −10 (was −22)
 *
 * Act 1 is unchanged, so the fight a player meets first plays exactly as
 * it always did.
 */
export function bloodPriceReduction(act: number): number {
  const acts = Math.max(1, Math.floor(act || 1))
  const table = TUNING.acts.scaling
  const gauntlet = gauntletRampMult(acts - table.length)
  const scale = (table[Math.min(acts, table.length) - 1]?.reqMult ?? 1) * gauntlet
  return Math.max(
    BLOOD_PRICE_REDUCTION,
    Math.round(BLOOD_PRICE_REDUCTION * scale ** BLOOD_PRICE_SCALE_EXP),
  )
}

export function totalRequired(required: Stats): number {
  return ELEMENTS.reduce((sum, element) => sum + required[element], 0)
}

/**
 * Coverage per gamedesign.md §17: sum of min(team, required) over required
 * elements only, divided by total required. 0..1.
 */
export function coverage(required: Stats, team: Stats): number {
  const total = totalRequired(required)
  if (total <= 0) return 1
  let covered = 0
  for (const element of ELEMENTS) {
    covered += Math.min(team[element], required[element])
  }
  return Math.min(covered / total, 1)
}

/**
 * Exact percentage shown to the player; 100 means guaranteed success.
 * Coverage relics apply in a fixed order: Missing Piece (virtual point)
 * → base chance → Clean Coverage → Specialist Badge → Desperation Coin
 * → Lucky Compass, each reading the running value, capped at 100. Raw
 * coverage() stays untouched for damage/XP.
 *
 * `curveExp` maps coverage^exp before relics; near-misses get more
 * forgiving as exp drops below 1. The live game uses the TUNING default
 * (0.65 since v0.25); simulations may pass overrides. `teamSize` powers
 * Specialist Badge (+10% on exactly-one-creature dispatches) — pass it
 * wherever the dispatched team is known.
 */
/** v3.33 (per Javi): the near-miss forgiveness fades by act. Act 1 keeps
 * the classic 0.65 softening, act 2 eases to 0.9, act 3 and every
 * gauntlet act beyond roll honest linear coverage. No run → act 1. */
export function curveExpForAct(act?: number): number {
  const ladder = TUNING.missions.successCurveExpByAct
  const index = Math.max(1, Math.min(act ?? 1, ladder.length)) - 1
  return ladder[index] ?? TUNING.missions.successCurveExp
}

export function successChance(
  required: Stats,
  team: Stats,
  run?: RunState | null,
  curveExp?: number,
  teamSize?: number,
): number {
  // Explicit exponents (sim experiments) still win; everything else
  // reads the act-faded ladder from the run it was handed.
  const exp = curveExp ?? curveExpForAct(run?.act)
  const total = totalRequired(required)
  if (total <= 0) return 100
  let covered = 0
  const uncoveredRequired: string[] = []
  let allRequiredTouched = true
  for (const element of ELEMENTS) {
    if (required[element] <= 0) continue
    covered += Math.min(team[element], required[element])
    if (team[element] <= 0) {
      uncoveredRequired.push(element)
      allRequiredTouched = false
    }
  }
  // Missing Piece (reworked v0.69): a virtual point always tops up the
  // weakest requirement. The old "exactly one hole → +1 and +10%" rule
  // INVERTED the math — token-touching the hole with a real point turned
  // the relic off, so MORE coverage showed a LOWER chance (Javi's
  // brother's find). Adding stats must never lower the chance.
  const missingPiece = !!run && hasRelic(run, 'relic-missing-piece')
  if (missingPiece && covered < total) {
    covered += 1
    // The virtual point plugs a single hole for Clean Coverage synergy.
    if (uncoveredRequired.length === 1) allRequiredTouched = true
  }
  let chance = Math.round(Math.min(covered / total, 1) ** exp * 100)
  if (run) {
    if (hasRelic(run, 'relic-clean-coverage') && allRequiredTouched) chance += 10
    if (hasRelic(run, 'relic-specialist-badge') && teamSize === 1) chance += 15
    // 👯 Twinned Charm: v2.83 measured this at 9.13 — the strongest
    // relic in the pool — because two-creature missions are far more
    // common than solo ones, making +20% nearly unconditional. Nerfed
    // to +10% (v2.84, per Javi).
    if (hasRelic(run, 'relic-twinned-charm') && teamSize === 2) chance += 10
    // 🗝️ Reliquary Key: relic COUNT becomes a stat. Counts itself.
    // v2.84 doubled it to 2%/relic for ~5–8-relic runs, but gauntlet
    // hoards of 20+ relics turned that into a flat +40 everywhere —
    // back to 1% (v3.32, per Javi). v4.82 (per Javi): 0.5%/relic,
    // rounded UP, capped at +20 — a 40-relic hoard maxes the key.
    if (hasRelic(run, 'relic-reliquary-key')) chance += Math.min(20, Math.ceil(run.relics.length * 0.5))
    // 👑 Pauper's Crown: broke and dangerous. 25 → 10 (v3.32, per
    // Javi): zero-gold was trivially easy to hold. 10 → 15 (v4.14):
    // with the 45-gold purse (v4.13) the crown now WIPES your gold on
    // pickup and staying broke is a real price again.
    if (hasRelic(run, 'relic-paupers-crown') && run.gold <= 0) chance += 15
    // Desperation Coin (reworked v0.69): raise desperate missions TOWARD
    // 50 instead of a flat +25 — the old cliff meant one more stat point
    // could drop the shown chance from 74 to 52 (same inversion class as
    // old Missing Piece).
    if (hasRelic(run, 'relic-desperation-coin') && chance < 50) {
      chance = Math.min(50, chance + 25)
    }
    if (hasRelic(run, 'relic-lucky-compass') && chance >= 65 && chance < 100) chance += 10
  }
  return Math.min(100, Math.max(0, chance))
}

/**
 * A mission's live creature capacity: Purpose Charm adds a slot, and a
 * mission never offers more slots than the player has creatures.
 */
export function missionSlots(mission: Mission, run?: RunState | null): number {
  // 🎓 Training takes exactly one student — no charm changes that.
  if (mission.training) return 1
  // 🍼 Normal mode: every mission fits one MORE creature (v1.88, per
  // Javi) — stacks with Purpose Charm; ascensions drop the freebie.
  const normalBonus = run && run.ascension === 0 ? 1 : 0
  const withBonus = mission.slots + normalBonus + (run && hasRelic(run, 'relic-purpose-charm') ? 1 : 0)
    + (mission.goldReward && hasStarweave(run, 'command-signature') ? 1 : 0)
  return run ? Math.max(1, Math.min(withBonus, run.roster.length)) : withBonus
}

export interface ChanceMods {
  journal?: boolean
  grudge?: boolean
  bloodPact?: boolean
  tithe?: boolean
  deluge?: boolean
  bloodPactBonus?: number
}

/**
 * Shared NON-BOSS chance pipeline (v0.81): flat boosts (Team Journal
 * +10, 😤 Grudge +20, 🩸 Blood Pact +10), the fire passive, then
 * Gambler's Soul's toward-60 raise. The team-builder preview, dispatch
 * (assignedChance) and resolution (computeMissionOutcome) MUST all run
 * through this so the shown number never shifts between screens.
 * All additions are flat or toward-a-floor — monotonic per v0.69.
 */
export function adjustedTeamChance(
  base: number,
  team: Creature[],
  mods: ChanceMods = {},
  run?: RunState | null,
  mission?: Mission,
): number {
  let chance = base
  if (mods.journal) chance += 10
  if (mods.tithe) chance += CREATURE_SKILL_VALUES.titheChance
  if (mods.deluge) chance += CREATURE_SKILL_VALUES.delugeChance
  if (mods.grudge) chance += 20
  if (mods.bloodPact) chance += mods.bloodPactBonus ?? 10
  chance = Math.min(100, chance + passiveChanceBonus(team, run, mission))
  chance = Math.max(chance, ballastFloor(team, mission))
  if (teamHasTrained(team, 'gamblers-soul') && chance < 60) {
    chance = Math.min(60, chance + 15)
  }
  return Math.min(100, chance)
}

/**
 * Failed assigned missions deal reduced damage (gamedesign.md §19):
 * damage × (1 − coverage), rounded up, minimum 1.
 */
export function failureDamage(missionDamage: number, cov: number): number {
  return Math.max(1, Math.ceil(missionDamage * (1 - cov)))
}

/** 🛡️ v3.33 (per Javi): a WON mission can never finish the run — its
 * chip stops at 1 HP. Losses keep their full teeth. Returns the chip
 * that actually lands. */
export function successChipClamp(hp: number, chip: number): number {
  return Math.max(0, Math.min(chip, hp - 1))
}

/** Adds XP and converts full thresholds into levels + pending stat points.
 * 🌟 Star Pupil (trained skill) doubles every grant at the source. */
export function grantXp(creature: Creature, amount: number, run?: RunState | null, exact = false) {
  if (!Number.isFinite(amount) || amount <= 0) return
  // 🎓 v7.40 (per Javi): no XP and no level-ups in the practice - its beats
  // pay 0, and nothing else (a passive, a trained skill) may grant any there:
  // Cinderfang's unexplained gold "+1" came from a practice level-up.
  if (run?.mode === 'tutorial') return
  const boosted = !exact && hasTrainedSkill(creature, 'star-pupil') ? amount * 2 : amount
  creature.xp += Math.max(0, boosted)
  while (creature.xp >= millenniaXpCost(creature, TUNING.xp.toNextLevel(creature.level))) {
    creature.xp -= millenniaXpCost(creature, TUNING.xp.toNextLevel(creature.level))
    creature.level++
    // 🌘 Eclipse Bloom (v3.16, per Javi): Duskmoth Empress NEVER gains
    // stat points from leveling — her growth is the node-end bloom
    // alone. Keyed off the innate def so temporary passive copies
    // (Understudy/Refract) don't strip a teammate's points.
    if (getCreatureDef(creature.defId).passive !== 'eclipse-bloom') creature.pendingStatPoints++
    // 🌱 Millennia (v3.04; REFOCUSED v5.33 per Javi): the ages feed the
    // creature's TWO HIGHEST elements — +N to the top, half (rounded up)
    // to the runner-up — so early allocations decide what it grows into.
    const millennia = millenniaPerLevel(creature)
    if (millennia > 0) {
      const ranked = [...ELEMENTS].sort((a, b) => creature.stats[b] - creature.stats[a])
      creature.stats[ranked[0]!] += millennia
      if (ranked[1]) creature.stats[ranked[1]] += Math.ceil(millennia / 2)
    }
    // 🌱 Evergrowth (v2.91): level-ups queue until a Rootwarden absorbs
    // them (evergrowthAbsorb) — grantXp cannot see the roster from here.
    creature.levelUpsUncounted = (creature.levelUpsUncounted ?? 0) + 1
    if (run && hasStarweave(run, 'growth-anchor')) {
      const node = ensureStarweaveNode(run)!
      // Pick AFTER the donor levels; stable roster order breaks recruitment
      // ties. Debit the amplified XP before recursion, so even self-mentoring
      // and passive chains can award no more than nine XP this node.
      const recipient = run.roster.reduce<Creature | undefined>((lowest, c) => !lowest || c.level < lowest.level ? c : lowest, undefined)
      if (recipient && node.mentorshipXp < 9) {
        const lesson = Math.min(9 - node.mentorshipXp, hasTrainedSkill(recipient, 'star-pupil') ? 6 : 3)
        node.mentorshipXp += lesson
        grantXp(recipient, lesson, run, true)
      }
    }
  }
}

/** A creature's live travel speed: innate speed, the water passive,
 * then relic bonuses; min 1. (uniformSpeed flattens everyone when set.) */
export function effectiveSpeed(creature: Creature, run?: RunState | null): number {
  if (TUNING.travel.uniformSpeed > 0) return TUNING.travel.uniformSpeed
  let speed = creature.speed
  // 💨 Momentum quickens, ⚓ Impulse weighs down (floor 1 below). v7.63 (per
  // Javi, "impulse slow feels really bad"): ONE point each in total, however
  // high the stacks - travel is distance over speed, so every point lost at
  // the low end was +50 % then +100 %, and the convoy moves at its slowest.
  speed += Math.min(1, creature.momentumStacks ?? 0) - Math.min(1, creature.impulseStacks ?? 0)
  // 🥁 Pacesetter (v3.37, per Javi): traded its instant walk home for a
  // flat +2 Speed — a drummer sets the convoy's pace, both ways.
  if (hasTrainedSkill(creature, 'pacesetter')) speed += 2
  const water = creaturePassives(creature).find(p => p.element === 'water')
  if (water) {
    speed = Math.round(speed * PASSIVE_VALUES.waterSpeedMult[water.tier - 1]!)
  }
  if (run && hasRelic(run, 'relic-swift-boots')) speed += 1
  speed += starweaveSpeedBonus(creature, run)
  return Math.max(1, speed)
}

/** Is completing this boss node the END of the run? Act-3 bosses are —
 * except on Ascension 10 (v0.84), where a 🤫 hidden fourth boss still
 * waits: the act-3 kill is NOT final until the secret one is revealed
 * (the revealed secret node itself passes this check). */
export function isFinalBossNode(state: RunState | null | undefined, node: { type: string } | null | undefined): boolean {
  if (!state || node?.type !== 'boss') return false
  if (state.act < TUNING.acts.count) return false
  if (ascensionSecretBoss(state.ascension ?? 0) && !state.secretBossRevealed) return false
  return true
}

/** A team travels as a convoy at its slowest member's pace — unless a
 * tier-3 Current Rider is aboard (v0.68): the tide-warp CARRIES the
 * whole team at the rider's own pace. */
export function teamSpeed(creatures: Creature[], run?: RunState | null): number {
  if (creatures.length === 0) return 1
  const convoy = Math.min(...creatures.map(c => effectiveSpeed(c, run)))
  const riders = creatures
    .filter(c => creaturePassives(c).some(p => p.element === 'water' && p.tier >= 3))
    .map(c => effectiveSpeed(c, run))
  return Math.max(convoy, ...riders)
}

/**
 * Rolls a recruit's stat spread: base elements plus one random point per
 * extra level, biased 70% toward its current two strongest elements so
 * recruits trend toward the two-element sweet spot.
 */
export function rollRecruitStats(base: Stats, extraPoints: number): Stats {
  const stats = { ...base }
  for (let i = 0; i < extraPoints; i++) {
    let element: (typeof ELEMENTS)[number]
    if (Math.random() < 0.7) {
      const topTwo = [...ELEMENTS].sort((a, b) => stats[b] - stats[a]).slice(0, 2)
      element = topTwo[Math.floor(Math.random() * topTwo.length)]!
    } else {
      element = ELEMENTS[Math.floor(Math.random() * ELEMENTS.length)]!
    }
    stats[element]++
  }
  return stats
}

/** Safety Net covers the first damaging failure OR expiry per encounter. */
export function safetyNetAvailable(state: RunState | null | undefined): boolean {
  return !!state && hasPassive(state, 'safety-net') && !state.encounter?.safetyNetUsed
}

/** onMissionSpawn hook: expire-timer relics, clamped to the minimum. */
export function modifiedExpireTimer(
  state: RunState | null | undefined,
  base: number,
  spawnIndex: number,
): number {
  if (!state) return base
  let expire = base
  if (hasRelic(state, 'relic-calm-hourglass') && spawnIndex < 5) expire += 10
  if (hasRelic(state, 'relic-short-fuse')) expire -= 2
  // 🌅 Morning Chorus (v2.80): roster-wide, so it counts whether or not
  // the singer is the one dispatched.
  expire += morningChorusBonus(state.roster, spawnIndex)
  return Math.max(MIN_EXPIRE_TIMER, expire)
}

/**
 * Warning Bell: dispatching to a mission with under 10s on its expire
 * timer boosts the convoy's travel speed. Speed bonus, applied at
 * dispatch time only (the trip out and home both benefit).
 */
export const WARNING_BELL_SPEED_BONUS = 2

export function dispatchSpeed(
  state: RunState | null | undefined,
  team: Creature[],
  expireTimerAtDispatch: number,
): number {
  // Preview the node buffs this dispatch unlocks without consuming them.
  // Snapshot every tied slowest member together before Coordinated Arrival.
  const speeds = team.map(c => effectiveSpeed(c, state) + starweaveDispatchBonus(c, team, state).speed)
  const slowest = speeds.length ? Math.min(...speeds) : 1
  if (team.length >= 3 && hasStarweave(state, 'command-right')) {
    for (let i = 0; i < speeds.length; i++) if (speeds[i] === slowest) speeds[i]! += 1
  }
  const riders = team.flatMap((c, i) => creaturePassives(c).some(p => p.element === 'water' && p.tier >= 3) ? [speeds[i]!] : [])
  let base = Math.max(speeds.length ? Math.min(...speeds) : 1, ...riders) + tideDelugeRider(team).speed
  // 💨 Tailwind (v2.82): Speed banked by Tailwind creatures already sent
  // this node. Its own trip only benefits at Apotheosis.
  base += tailwindSpeed(team, state?.encounter?.tailwindSpeed ?? 0)
  if (state && hasRelic(state, 'relic-warning-bell') && expireTimerAtDispatch < 10) {
    return base + WARNING_BELL_SPEED_BONUS
  }
  return base
}

/**
 * Steady Clock: each successful mission grants every waiting mission +5
 * seconds before expiring. Returns the bonus (0 without the relic).
 */
export function steadyClockBonus(state: RunState | null | undefined): number {
  return state && hasRelic(state, 'relic-steady-clock') ? 5 : 0
}

/**
 * onMissionAssigned hook: fight-timer relics, clamped to the minimum.
 * Focus Bell speeds up the first 3 assignments per encounter by 35%
 * (`focusBellAvailable` = fewer than 3 uses so far).
 */
export function modifiedFightTimer(
  state: RunState | null | undefined,
  focusBellAvailable: boolean,
  base: number,
): { fightTimer: number; usedFocusBell: boolean } {
  if (!state) return { fightTimer: base, usedFocusBell: false }
  let fight = base
  if (hasRelic(state, 'relic-quick-whistle')) fight -= 2
  let usedFocusBell = false
  if (hasRelic(state, 'relic-focus-bell') && focusBellAvailable) {
    usedFocusBell = true
    fight = Math.ceil(fight * 0.65)
  }
  return { fightTimer: Math.max(MIN_FIGHT_TIMER, fight), usedFocusBell }
}

/**
 * What a mission will cost or grant when it resolves — the single source
 * of truth for XP/safety relic effects, shared by the live game and the
 * rarity simulation. Chance-dependent relics key off the success chance
 * captured at assignment time.
 */
export function computeMissionOutcome(
  state: RunState | null | undefined,
  mission: Mission,
  // v3.33: undefined → act-faded exponent from the state (see
  // curveExpForAct); sims may still pin an explicit value.
  curveExp?: number,
) {
  const enc = state?.encounter
  // Pick order matters (Cursed Slot punishes the FIRST creature chosen).
  const team = mission.assignedCreatureIds
    .map(id => state?.roster.find(c => c.id === id))
    .filter((c): c is Creature => !!c)
  const stats = missionTeamStats(mission, team, state)
  const cov = coverage(mission.required, stats)
  const isBoss = mission.tier === 'boss'
  let chance = successChance(mission.required, stats, state, curveExp, team.length)
  // Shared non-boss pipeline (journal/grudge/pact, fire passive,
  // Gambler's Soul) — the builder preview and dispatch use it too, so
  // the shown number never shifts between screens.
  if (!isBoss) {
    chance = adjustedTeamChance(chance, team, {
      journal: !!mission.journalBoosted,
      grudge: !!mission.grudgeBoosted,
      bloodPact: !!mission.bloodPactPaid,
      tithe: !!mission.titheBoosted,
      deluge: !!mission.delugeBoosted,
      bloodPactBonus: bloodPactBonus(team),
    }, state, mission)
  }
  // Boss fights (§18): raw coverage IS the win chance — no curve, no
  // chance relics. Win or lose, the player always takes the missing
  // coverage share as damage (0 at full coverage).
  if (isBoss) chance = curseAdjustedChance(mission, Math.round(cov * 100), team.length)
  // 👑 Decree (v3.59 buff, per Javi): a royal order WINS. Forcing the
  // chance to 100 here rather than special-casing the result is what
  // makes the rest fall in line for free — the wheel skips its fake
  // suspense, and the missing-coverage chip that lands even on a win
  // (v0.72) works out to zero, so a decree cannot cost HP either.
  if (mission.decreed || mission.eaten) chance = 100 // 🌀 v6.91: a swallowed mission never rolls either
  // ⚖️ Iron Week (v5.66, weekly modifier): no dice. The shown chance is
  // forced like a Decree, so the wheel skips its fake suspense — but
  // the PRICE below keeps reading the honest coverage-derived number
  // (priceChance), so a thin team still pays its posted shortfall
  // chip. Bosses obey a harder law: FULL coverage or nothing — the
  // wall survives determinism. (Decree captured at 100 → still free.)
  let priceChance = chance
  if (hasWeeklyMod(state, 'iron') && !mission.training) {
    chance = isBoss ? (cov >= IRON_BOSS_COVERAGE ? 100 : 0) : 100
  }
  const atDispatch = mission.assignedChance ?? chance

  let xpOnSuccess = mission.xp
  if (state) {
    if (hasPassive(state, 'training-manual')) xpOnSuccess += TUNING.bonuses.trainingManualBonusXp
    if (hasRelic(state, 'relic-short-fuse')) xpOnSuccess += 3
    if (hasRelic(state, 'relic-solo-practice') && team.length === 1) xpOnSuccess += 2
    if (hasRelic(state, 'relic-study-group') && team.length >= 2) {
      xpOnSuccess += team.length >= 3 ? 5 : 3
    }
    if (hasRelic(state, 'relic-risk-taker') && atDispatch < 60) xpOnSuccess += 2
    // Perfect Plan: EVERY 100% mission grants the bonus (was first only).
    if (hasRelic(state, 'relic-perfect-plan') && atDispatch >= 100) xpOnSuccess += 5
  }

  let xpOnFailure = Math.round(mission.xp * cov * TUNING.xp.failureXpMult)
  if (state && hasRelic(state, 'relic-shared-lessons')) xpOnFailure += 3

  // v0.72 (per Javi): the number the player SEES is the number that
  // bites. Success chip = the missing share of the FINAL chance (relic
  // and passive bonuses included) — a shown 100% takes zero damage,
  // full stop. (Supersedes v0.47's raw-coverage chip and the v0.71
  // mercy rule that patched around it.) Bosses are unchanged by
  // construction: their coverage IS their chance. Failures keep their
  // ceil/min-1 sting on top.
  // 🛡️ Aegis (trained skill): wins it joins never chip.
  // 🧢 v5.20 (per Javi: "doing a mission with lower percent win damages
  // you more than expiring it"): WINNING can never hurt more than
  // walking away — non-boss win chips cap at the BASE expiry cost.
  // Deliberately unsoftened (not expiryDamageFor): routing through the
  // softeners would let a Cremation Apotheosis zero every win chip.
  // Bosses keep the full missing share — coverage IS their contract.
  const expiryCap = Math.max(1, Math.round(mission.damage * TUNING.encounters.expiredDamageMult))
  const rawMissing = teamHasTrained(team, 'aegis')
    ? 0
    : Math.max(0, Math.round(mission.damage * (1 - priceChance / 100)))
  const missingDamage = isBoss ? rawMissing : Math.min(rawMissing, expiryCap)
  let failDamage = isBoss
    ? Math.max(0, Math.round(mission.damage * (1 - priceChance / 100)))
    : failureDamage(mission.damage, cov)
  // Corrupted Element: every point committed OVER the corrupted
  // requirement bites back with extra damage — but only on a failure.
  if (mission.bossSkill === 'corrupted' && mission.corruptedElement) {
    const over = Math.max(0, stats[mission.corruptedElement] - mission.required[mission.corruptedElement])
    failDamage += over * CORRUPTED_DAMAGE_PER_POINT
  }
  // Faust's curses sharpen failure: Doubt adds +10, Excess triples it.
  if (mission.bossSkill === 'curse') {
    if (mission.curseChoice === 2) failDamage += 10
    if (mission.curseChoice === 3) failDamage *= 3
  }
  // Stoneguard (earth passive): the team absorbs part of a failure.
  failDamage = Math.max(0, failDamage - passiveFailureReduction(team))
  // 🧱 Bulwark Drop (v2.92): the armed wall absorbs this failure whole.
  if (state?.encounter?.bulwarkArmed) failDamage = 0
  // 🛡️ Bulwark (v3.00): the Sentinel's own shield does the same for its
  // team, and blocks the injuries too.
  if (bulwarkAboard(team)) failDamage = 0
  if (!isBoss && state && hasRelic(state, 'relic-safety-margin')) failDamage = Math.max(0, failDamage - 1)
  if (!mission.training) failDamage = Math.max(0, failDamage - starweaveFailureReduction(state))
  // 🎩 v6.88 Mr. Khool: COOL HEAD halves what a lost bet costs, then the Wager scales the loss.
  // 🎲 v7.79: a win's purse is FLAT - its XP rides the mission's XP (Sundial, Overkill and friends still scale it,
  // as they scaled the old bet), its gold is paid at resolution (wagerGold below), never for an eaten mission.
  if (composureApotheosis(team)) failDamage = Math.ceil(failDamage / 2)
  const forcedRoll = !!mission.decreed || !!mission.eaten || (hasWeeklyMod(state, 'iron') && !mission.training)
  const wager = wagerSteps(mission, team, forcedRoll ? chance : undefined)
  const purse = wagerPurse(wager, state?.act)
  if (wager > 0) {
    xpOnSuccess += purse.xp
    failDamage = Math.round(failDamage * wagerDamageMult(wager))
  }
  // 🌀 v6.91 Blackhole: eaten, not completed - the stolen statline is the whole payment.
  if (mission.eaten) xpOnSuccess = 0
  // 🏗️ v6.92 Robodude's Overkill: land far past full coverage and the team's XP doubles
  // (his permanent point is paid at resolution, see applyResolution).
  const overkill = !mission.training && !mission.eaten && overkillTriggered(mission, team, stats)
  if (overkill) xpOnSuccess *= 2
  // Safety Net never covers a boss — the attrition cost is the fight.
  // 🕸️ v4.18 (Javi's Yin & Yang report): split PIECES are nettable —
  // they're boss-tier for coverage math, but a fragment's failure is a
  // regular loss, not the boss-attrition rule.
  // 🕸️ v7.77 (Stoneguard review): only a DAMAGING failure spends it (its doc, and the expiry path) - one the
  // guard, Thornmail or a Bulwark already zeroed leaves the net for the next real hit.
  const safetyNet = failDamage > 0 && (!isBoss || isBossSplit(mission)) && safetyNetAvailable(state)

  const injuriesOff = !injuriesActive(state)
  const medicBand = !injuriesOff && !!state && hasPassive(state, 'medic-band') && !enc?.medicBandUsed
  // Close Enough at 85% (v0.77; 80 was still too easy for a Legendary).
  const closeEnough = !injuriesOff && !!state && hasRelic(state, 'relic-close-enough') && atDispatch >= 85

  return {
    team,
    cov,
    chance,
    /** 🪙 The gold wedge: a share of `chance` that pays extra (v2.71). */
    windfall: mission.eaten ? 0 : windfallChance(state, mission, stats, chance),
    xpOnSuccess,
    xpOnFailure,
    /** 🏗️ v6.92: Overkill landed - Robodude's permanent point is owed on success. */
    overkill,
    /** 🎲 v7.79: The Wager's steps on this mission, and the flat gold a win pays (to the wallet, any mission). */
    wagerSteps: wager,
    wagerGold: mission.eaten || wager <= 0 ? 0 : purse.gold,
    damageOnFailure: safetyNet ? 0 : failDamage,
    /** The missing share of the FINAL chance hurts even on a win (v0.72).
     * 🛡️ v3.95 (Javi: "pretty sure I just [took] damage with it on"): the
     * Sentinel's Bulwark zeroes this too — its blurb reads "its next
     * mission deals ZERO damage even on failure", and "even on failure"
     * includes the win-chip a fortiori. Wins outnumber losses, so the
     * shield looked broken precisely when it was used well. Bulwark DROP
     * stays failure-only by its own wording ("the next failed mission"). */
    damageOnSuccess: bulwarkAboard(team) ? 0 : missingDamage,
    usesSafetyNet: safetyNet,
    usesMedicBand: medicBand && !closeEnough,
    injuriesPrevented: injuriesOff || medicBand || closeEnough || bulwarkAboard(team) || !!state?.encounter?.bulwarkArmed,
  }
}
